import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Tooltip } from '../../../../../components/ds';
import {
    FiX, 
    FiSliders, 
    FiActivity, 
    FiSettings, 
    FiTrash2,
    FiChevronUp,
    FiDroplet,
    FiPlus,
    FiDownload,
    FiRefreshCw,
    FiTarget,
    FiShield,
    FiClock,
    FiLayers
} from 'react-icons/fi';
import {
    defaultLogicInputs,
    COMPARATOR_OPS,
    resolveSmartLabel as resolveSmartLabelUtil,
    findAssociatedEntryNode,
    resolveAutoText,
    resolveAuthoredName,
    getCustomName,
    CONDITION_NAME_NODE_TYPES,
} from '../utils/nodeUtils';
import { LOGIC_NODE_TYPES } from '../config/nodeVisualConfig';
import {
    linkOperandInput,
    eligibleOperandSources,
    matchesTargetInput,
    resolveSourceFields,
    resolveTargetHandle,
    setOperandConstant,
} from '../utils/operandLinking';
import { v4 as uuidv4 } from 'uuid';
import LogicNode from '../nodes/LogicNode';
import ChartStylingSection from './ChartStylingSection';
import { defaultOutputStyles } from '../utils/chartDataDerivation';
import IndicatorAboutSection, { hasParamHelp, fmtParamValue } from './IndicatorAboutSection';
import ModelVersionSelect from './ModelVersionSelect';

// --> Available input types for IF Condition
const INPUT_OPTIONS = [
    { value: 'RSI', label: 'RSI (Relative Strength Index)' },
    { value: 'EMA', label: 'EMA (Exponential Moving Average)' },
    { value: 'SMA', label: 'SMA (Simple Moving Average)' },
    { value: 'MACD', label: 'MACD' },
    { value: 'CLOSE', label: 'Close Price' },
    { value: 'OPEN', label: 'Open Price' },
    { value: 'HIGH', label: 'High Price' },
    { value: 'LOW', label: 'Low Price' },
    { value: 'VOLUME', label: 'Volume' },
    { value: 'ATR', label: 'ATR (Average True Range)' },
    { value: 'BOLLINGER_UPPER', label: 'Bollinger Upper Band' },
    { value: 'BOLLINGER_LOWER', label: 'Bollinger Lower Band' },
];

const TIMEFRAME_OPTIONS = ['1m', '3m', '5m', '15m', '30m', '1h', '2h', '4h', '1d', '1w'];

/**
 * NodePropertiesPanel Component
 */
//--> R9 / R13 — condition Name + Notes, stored ONLY in the shared visual-overrides record (custom_name /
//*   notes, keyed by node id) through the Builder's display chokepoint `updateNodeData` -> the autosave
//*   hook. Never node data (R10-T8). Caps are Unicode code points (Array.from), matching the backend
//*   len(str) check (AC7.2/7.3, AC9.4). A cleared field writes a null leaf = delete (AC9.6). Before the
//*   first save there is no record to write to: both fields are disabled with a hint (AC9.7).
const CONDITION_TEXT_CAPS = { custom_name: 80, notes: 2000 };
const capCodePoints = (value, cap) => Array.from(value || '').slice(0, cap).join('');

const ConditionNameNotesFields = ({ nodeId, strategyId, entry, legacyName = null, autoText, updateNodeData, lockHint = null }) => {
    //! F-arb-4 — a saved strategy whose display record is not writable (loading / failed / read-only) locks
    //  these too: the store would refuse the write, so the field must not look editable.
    const enabled = !!strategyId && !lockHint;
    //* 50-21 LV — same rule as the display (resolveAuthoredName): record custom_name, else the carried-over
    //  legacy data.label. Typing writes only the record; clearing writes null and the display then falls back
    //  to the legacy label (AC9.6 semantics).
    const name = typeof entry?.custom_name === 'string' ? entry.custom_name : (legacyName || '');
    const notes = typeof entry?.notes === 'string' ? entry.notes : '';
    const write = (key, raw) => {
        if (!enabled) return;
        const capped = capCodePoints(raw, CONDITION_TEXT_CAPS[key]);
        updateNodeData(nodeId, { [key]: capped === '' ? null : capped });
    };
    const remaining = (value, key) => CONDITION_TEXT_CAPS[key] - Array.from(value).length;
    const hint = enabled ? null : (
        <p className="text-[10px] text-fg-3 italic mt-1">{lockHint || 'Save the strategy first'}</p>
    );
    const inputCls = 'w-full bg-bg-3 dark:bg-bg-1 border border-[var(--divider)] rounded-md px-2 py-1.5 text-xs text-fg-1 disabled:opacity-60 disabled:cursor-not-allowed';
    return (
        <div className="space-y-3 bg-bg-3/40 dark:bg-bg-2 rounded-lg p-3 border border-[var(--divider)]" data-testid="condition-name-notes">
            <div>
                <div className="flex items-center justify-between mb-1">
                    <label htmlFor={`node-custom-name-${nodeId}`} className="text-xs font-medium text-fg-1">Name</label>
                    <span className="text-[10px] text-fg-3">{remaining(name, 'custom_name')} remaining</span>
                </div>
                <input
                    id={`node-custom-name-${nodeId}`}
                    type="text"
                    value={name}
                    disabled={!enabled}
                    placeholder={autoText}
                    onChange={(e) => write('custom_name', e.target.value)}
                    className={inputCls}
                />
                {hint}
                {(getCustomName(entry) || legacyName) && (
                    //* AC9.5 — the auto-assembled logic stays visible as read-only TEXT beside a custom name.
                    <p className="text-[10px] text-fg-3 mt-1 font-mono" data-testid="condition-auto-text">{autoText}</p>
                )}
            </div>
            <div>
                <div className="flex items-center justify-between mb-1">
                    <label htmlFor={`node-notes-${nodeId}`} className="text-xs font-medium text-fg-1">Notes</label>
                    <span className="text-[10px] text-fg-3">{remaining(notes, 'notes')} remaining</span>
                </div>
                <textarea
                    id={`node-notes-${nodeId}`}
                    rows={3}
                    value={notes}
                    disabled={!enabled}
                    placeholder="Why this condition exists, when to reuse it"
                    onChange={(e) => write('notes', e.target.value)}
                    className={inputCls}
                />
                {hint}
            </div>
        </div>
    );
};

const NodePropertiesPanel = ({ 
    selectedNode, 
    onClose, 
    updateNodeData,
    onDeleteNode,
    availableFields = { standard: [], calculated: [] },
    nodes = [],
    edges = [],
    onEdgeUpdate,
    allIndicatorMetadata = [],
    strategyScope = {},
    strategyId = null,
    visualOverrides = null,
    displayLockHint = null,
    graphWiring,
}) => {
    // 1. ALL HOOKS MUST BE AT TOP LEVEL
    const [isChartStylingOpen, setIsChartStylingOpen] = useState(true);
    const [linkRefusal, setLinkRefusal] = useState('');

    //! slice 5: which parameter's help is open (one at a time, per node)
    const [paramHelp, setParamHelp] = useState({ nodeId: null, key: null });

    useEffect(() => {
        setLinkRefusal('');
    }, [selectedNode?.id]);

    // --> Helper to generate descriptive labels for nodes (e.g., Comparator results)
    // --> Helper to generate descriptive labels for nodes (e.g., Comparator results)
    // --> Helper to generate descriptive labels using shared utility
    const resolveSmartLabel = useCallback((node) => {
        return resolveSmartLabelUtil(node, nodes, edges);
    }, [nodes, edges]);

    // --> Filter Potential Source Nodes
    const availableSources = useMemo(() => {
        return eligibleOperandSources(nodes, selectedNode)
            .map(n => ({
                id: n.id,
                label: resolveSmartLabel(n),
                type: n.type,
                data: n.data
            }));
    }, [nodes, selectedNode, resolveSmartLabel]);

    // --> Resolve the "Live" node from the global nodes array to ensure state sync
    const liveNode = useMemo(() => {
        return nodes.find(n => n.id === selectedNode?.id) || selectedNode;
    }, [nodes, selectedNode]);

    const nodeData = useMemo(() => {
        if (!liveNode || liveNode.type !== 'indicator') return liveNode?.data || {};
        
        const indicatorName = liveNode.data?.indicator;
        const allMetadata = Array.isArray(allIndicatorMetadata) ? allIndicatorMetadata : [];
        const canonicalMeta = allMetadata.find(m => m.name === indicatorName);
        
        if (!canonicalMeta) return liveNode.data || {};

        const healedData = { ...liveNode.data };
        const backendVisuals = canonicalMeta.visual_settings || {};

        // PROTOCOL ENFORCEMENT
        if (backendVisuals.type) {
            const bType = backendVisuals.type.toLowerCase();
            if (bType === 'custom' || bType === 'marker') {
                healedData.visual_settings = {
                    ...(healedData.visual_settings || {}),
                    type: bType
                };
            }
        }

        //* 50-7: backend metadata only FILLS a missing pane setting — the node's own
        //* `is_overlay` (written by the Pane toggle) wins, same precedence as
        //* resolveNodeOverlay. Overwriting it here made the toggle snap back every render.
        if (backendVisuals.is_overlay !== undefined && typeof liveNode?.data?.visual_settings?.is_overlay !== 'boolean') {
             healedData.visual_settings = {
                ...(healedData.visual_settings || {}),
                is_overlay: backendVisuals.is_overlay
            };
        }

        return healedData;
    }, [liveNode, allIndicatorMetadata]);

    // 2. NOW WE CAN HAVE EARLY RETURNS
    if (!selectedNode) return null;

    const commitOperandLink = ({ inputs, inputIndex, targetHandle, sourceId, field, edgeStyle, persistInput = true }) => {
        if (sourceId) {
            const removeEdgeIds = edges
                .filter(edge => matchesTargetInput(edge, liveNode.id, targetHandle, inputIndex))
                .map(edge => edge.id);
            const outcome = graphWiring.onConnect({
                source: sourceId,
                sourceHandle: field,
                target: liveNode.id,
                targetHandle,
            }, {
                removeEdgeIds,
                edgeStyle,
            });
            setLinkRefusal(outcome.ok ? '' : outcome.reason);
            return outcome;
        }
        const outcome = graphWiring.unlinkTarget({
            target: liveNode.id,
            targetHandle,
            inputIndex,
            persistInput,
        });
        setLinkRefusal(outcome.ok ? '' : outcome.reason);
        return outcome;
    };

    const commitOperandField = ({ inputs, inputIndex, targetHandle, field, persistInput = true }) => {
        const matchingEdges = edges.filter(edge => (
            matchesTargetInput(edge, liveNode.id, targetHandle, inputIndex)
        ));
        const resolvedHandle = resolveTargetHandle(liveNode, targetHandle, inputIndex);
        const currentInput = inputs?.find(input => String(input?.id) === String(resolvedHandle))
            || (Number.isInteger(inputIndex) ? inputs?.[inputIndex] : null);
        const sourceId = matchingEdges[0]?.source || currentInput?.nodeId;
        if (!sourceId) return { ok: false, reason: '' };
        return commitOperandLink({
            inputs,
            inputIndex,
            targetHandle,
            sourceId,
            field,
            edgeStyle: matchingEdges[0]?.style,
            persistInput,
        });
    };

    const commitOperandConstant = ({ inputs, inputIndex, targetHandle }) => {
        const result = setOperandConstant({
            inputs,
            edges,
            targetNode: liveNode,
            targetId: liveNode.id,
            targetHandle,
            inputIndex,
        });
        updateNodeData(liveNode.id, { inputs: result.inputs });
        if (onEdgeUpdate) onEdgeUpdate(result.edges);
        return result;
    };

    const nodeType = liveNode.type;

    // --> Determine panel title based on node type
    const getPanelTitle = () => {
        switch(nodeType) {
            case 'comparator': return 'LOGIC COMPARATOR';
            case 'logic_gate': return 'LOGIC GATE';
            case 'time_window': return 'TIME WINDOW';
            // Use smart label for indicator title
            case 'indicator': return resolveSmartLabel(liveNode).toUpperCase();
            case 'data_source': return 'DATA SOURCE CONFIG';
            case 'signal': 
            case 'position_trigger': return 'EXECUTION SIGNAL';
            case 'chart_node': return 'CHART CONTAINER';
            case 'candle_node': return 'CANDLESTICK CHART';
            case 'heikin_node': return 'HEIKIN ASHI';
            case 'volume_node': return 'VOLUME CHART';
            case 'line_node': return 'LINE CHART';
            case 'area_node': return 'AREA CHART';
            default: return 'NODE PROPERTIES';
        }
    };

    // --> Helper to get clean display name
    const getNodeDisplayName = () => {
         switch(nodeType) {
            case 'comparator': return 'Comparator';
            case 'logic_gate': return 'Logic Gate';
            case 'time_window': return 'Time Window';
            case 'indicator': return nodeData.label || nodeData.name || nodeData.indicator || 'Technical Indicator';
            case 'data_source': return 'Data Source';
            case 'chart_node': return 'Chart Container';
            case 'candle_node': return 'Candlestick Chart';
            case 'heikin_node': return 'Heikin Ashi Chart';
            case 'volume_node': return 'Volume Chart';
            case 'line_node': return 'Line Chart';
            case 'area_node': return 'Area Chart';
            case 'signal':
            case 'position_trigger': return 'Execution Signal';
            default: return nodeData.label || 'Node';
        }
    };

    // --> Reusable Info Section (Disabled per design)
    const renderNodeInfoSection = () => null;

    // --> Comparator Panel (Redesigned)
    const renderComparatorPanel = () => {
        // Ensure exactly 2 inputs exist
        const defaultInputs = [
            { type: 'link', offset: 0, id: '0' },
            { type: 'constant', value: 0, id: '1' } // Default B to constant 0
        ];
        
        const inputs = (nodeData.inputs && nodeData.inputs.length >= 2) 
            ? nodeData.inputs 
            : defaultInputs;

        // One-time init of inputs if missing
        if (!nodeData.inputs || nodeData.inputs.length < 2) {
            // We can't easily call updateNodeData inside render loop without infinite loop risk
            // rely on the UI to show defaults until user interacts, but better to sync 'em up on first change
        }

        const handleOpChange = (newOp) => {
            updateNodeData(selectedNode.id, { operator: newOp });
        };

        const handleInputChange = (idx, field, val) => {
            const newInputs = [...inputs];
            if (newInputs.length < 2) {
                 // hydration fix
                 newInputs[0] = newInputs[0] || { type: 'link', offset: 0, id: '0' };
                 newInputs[1] = newInputs[1] || { type: 'constant', value: 0, id: '1' };
            }
            
            if (field === 'offset') val = Math.max(0, parseInt(val) || 0);
            
            newInputs[idx] = { ...newInputs[idx], [field]: val };
            updateNodeData(selectedNode.id, { inputs: newInputs });
        };

        return (
             <div className="space-y-6 animate-fadeIn">
                {renderNodeInfoSection('Logic Comparison', 'Comparator')}

                {/* Operator Selector */}
                <div>
                    <label className="block text-[10px] font-bold text-fg-3 uppercase tracking-widest mb-2">
                        COMPARATOR OPERATOR
                    </label>
                    <div className="grid grid-cols-4 gap-1 rounded-xl overflow-hidden border border-[var(--divider)] bg-bg-2 p-1">
                        {Object.entries(COMPARATOR_OPS).map(([value, op]) => (
                            <Tooltip key={value} content={op.label} placement="top">
                            <button
                                onClick={() => handleOpChange(value)}
                                className={`py-2 text-[10px] font-bold uppercase tracking-wide rounded transition-all flex flex-col items-center justify-center gap-0.5 ${
                                    (nodeData.operator || 'gt') === value
                                        ? 'bg-[var(--node-math)] text-white shadow-sm'
                                        : 'text-fg-3 hover:text-fg-2 hover:bg-bg-3 dark:hover:bg-bg-3'
                                }`}
                                aria-label={op.label}
                            >
                                <span className="text-sm">{op.glyph}</span>
                            </button>
                            </Tooltip>
                        ))}
                    </div>
                     <div className="text-center mt-2 text-[10px] font-mono font-bold text-[var(--node-math)] uppercase">
                        {COMPARATOR_OPS[nodeData.operator || 'gt']?.label}
                    </div>
                </div>

                {/* Inputs A & B configuration */}
                <div className="space-y-4">
                    {['A', 'B'].map((label, idx) => {
                        const input = inputs[idx] || {};
                        const handleId = resolveTargetHandle(liveNode, input.id, idx);
                        const edge = edges.find(e => matchesTargetInput(e, liveNode.id, handleId, idx));
                        
                        const currentSourceId = edge ? edge.source : (inputs[idx]?.nodeId || '');
                        const currentField = edge ? (edge.sourceHandle || 'value') : (inputs[idx]?.field || 'value');
                        
                        const sourceNode = nodes.find(n => n.id === currentSourceId);
                        const fields = resolveSourceFields(sourceNode, availableFields, allIndicatorMetadata);

                        return (
                            <div key={idx} className="bg-bg-3 dark:bg-bg-2/50 p-3 rounded-xl border border-[var(--divider)] relative group animate-slideInRight">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-xs font-bold text-fg-3">Input {label}</span>
                                    
                                    {/* Type Toggle */}
                                    <div className="flex bg-bg-3 dark:bg-bg-3 rounded p-[2px]">
                                        <button
                                            onClick={() => handleInputChange(idx, 'type', 'link')}
                                            className={`px-2 py-0.5 rounded text-[9px] font-bold transition-all ${
                                                inputs[idx]?.type === 'link' ? 'bg-bg-3 text-[var(--node-visual)] shadow-sm' : 'text-fg-3'
                                            }`}
                                        >
                                            LINK
                                        </button>
                                        <button
                                            onClick={() => {
                                                commitOperandConstant({ inputs, inputIndex: idx, targetHandle: handleId });
                                            }}
                                            className={`px-2 py-0.5 rounded text-[9px] font-bold transition-all ${
                                                inputs[idx]?.type === 'constant' ? 'bg-bg-3 text-[var(--warning)] shadow-sm' : 'text-fg-3'
                                            }`}
                                        >
                                            NUM
                                        </button>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 gap-3">
                                    { inputs[idx]?.type === 'constant' ? (
                                        <div>
                                            <label className="block text-[8px] font-bold text-fg-3 uppercase mb-1">Constant Value</label>
                                            <input
                                                type="number"
                                                step="any"
                                                value={inputs[idx]?.value ?? 0}
                                                onChange={(e) => handleInputChange(idx, 'value', e.target.value)}
                                                onBlur={(e) => handleInputChange(idx, 'value', parseFloat(e.target.value) || 0)}
                                                className="w-full px-2 py-1.5 rounded bg-bg-2 border border-[var(--border)] text-xs font-mono font-bold outline-none focus:border-[var(--node-math)]"
                                                placeholder="0.0"
                                            />
                                        </div>
                                    ) : (
                                        <div className="space-y-2">
                                            {/* Source Select */}
                                            <div>
                                                <label className="block text-[8px] font-bold text-fg-3 uppercase mb-1">Linked Source</label>
                                                <select
                                                    className="w-full px-2 py-1.5 rounded bg-bg-2 border border-[var(--border)] text-xs font-bold outline-none focus:border-[var(--node-math)] truncate"
                                                    value={currentSourceId}
                                                    onChange={(e) => {
                                                        const newSourceId = e.target.value;
                                                        const newSourceNode = nodes.find(n => n.id === newSourceId);
                                                        const resolvedFields = resolveSourceFields(newSourceNode, availableFields, allIndicatorMetadata);
                                                        const defaultField = resolvedFields.includes('close') ? 'close' : (resolvedFields[0] || 'value');
                                                        commitOperandLink({
                                                            inputs,
                                                            inputIndex: idx,
                                                            targetHandle: handleId,
                                                            sourceId: newSourceId,
                                                            field: defaultField,
                                                            edgeStyle: { strokeWidth: 2, stroke: 'var(--node-comparator)' },
                                                        });
                                                    }}
                                                >
                                                    <option value="">Select Source...</option>
                                                    {availableSources.map(src => (
                                                        <option key={src.id} value={src.id}>
                                                            {src.type === 'constant' ? '[CONST] ' : src.type === 'data_source' ? '[MKT] ' : src.type === 'math' ? '[MATH] ' : src.type === 'comparator' ? '[COMP] ' : src.type === 'logic_gate' ? '[LOGIC] ' : '[IND] '}
                                                            {src.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>

                                            {/* Field Select */}
                                            {(() => {
                                                if (!edge && !currentSourceId) return null;
                                                if (fields.length === 0) return null;

                                                return (
                                                    <div>
                                                        <label className="block text-[8px] font-bold text-fg-3 uppercase mb-1">Source Field</label>
                                                        
                                                        {fields.length <= 1 ? (
                                                            <div className="px-2 py-1.5 rounded bg-bg-3 dark:bg-bg-2/50 border border-[var(--divider)] text-[10px] font-mono text-fg-3 flex items-center justify-between h-[30px]">
                                                                <span className="truncate">{(fields[0] || 'VALUE').toUpperCase()}</span>
                                                                <span className="text-[8px] uppercase bg-bg-3 dark:bg-bg-3 px-1 rounded ml-2">Auto</span>
                                                            </div>
                                                        ) : (
                                                             <select
                                                                className="w-full px-2 py-1.5 rounded bg-bg-2 border border-[var(--border)] text-[10px] font-mono font-bold outline-none focus:border-[var(--node-math)] h-[30px]"
                                                                value={currentField}
                                                                onChange={(e) => {
                                                                    const newField = e.target.value;
                                                                    commitOperandField({
                                                                        inputs,
                                                                        inputIndex: idx,
                                                                        targetHandle: handleId,
                                                                        field: newField,
                                                                    });
                                                                }}
                                                            >
                                                                {fields.map((f, i) => (
                                                                    <option key={`${f}_${i}`} value={f}>{f.toUpperCase()}</option>
                                                                ))}
                                                            </select>
                                                        )}
                                                    </div>
                                                );
                                            })()}

                                            {/* Lookback Input (Only for Links) */}
                                            <div>
                                                <label className="block text-[8px] font-bold text-fg-3 uppercase mb-1">Lookback (Bars)</label>
                                                <div className="relative">
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        value={inputs[idx]?.offset || 0}
                                                        onChange={(e) => handleInputChange(idx, 'offset', e.target.value)}
                                                        className="w-full px-2 py-1.5 rounded bg-bg-2 border border-[var(--border)] text-xs font-mono font-bold outline-none focus:border-[var(--node-math)] h-[30px]"
                                                    />
                                                    <span className={`absolute right-2 top-1.5 text-[8px] font-bold uppercase pointer-events-none ${inputs[idx]?.offset > 0 ? 'text-[var(--warning)]' : 'text-fg-3'}`}>
                                                        {inputs[idx]?.offset > 0 ? 'PREV' : 'CURR'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    // --> Logic Gate Panel (Upgraded for Multi-Input)
    const renderLogicGatePanel = () => {
        const isUnary = (nodeData.operator || 'and') === 'not';
        // Auto-assign IDs for fresh inputs if missing (UI safeguard)
        const inputs = nodeData.inputs || defaultLogicInputs(nodeData.operator);

        const handleOpChange = (newOp) => {
            const op = newOp.toLowerCase();
            const updates = { operator: op };
            
            // --> Correct inputs based on operator type
            // Preserve existing inputs/IDs where possible
            if (op === 'not' && inputs.length > 1) {
                // Identify inputs to be removed (indices 1 to end)
                const removedInputs = inputs.slice(1);
                updates.inputs = [inputs[0]];

                // --> FILTER EDGES: Remove edges connected to the removed inputs
                if (onEdgeUpdate) {
                    const newEdges = edges.filter(edge => {
                        // Keep edge if it's NOT targeting this node
                        if (edge.target !== liveNode.id) return true;

                        // Check if edge is targeting one of the removed inputs
                        // Check both UUID and legacy index-based ID
                        const isTargetingRemovedInput = removedInputs.some((input, idx) => {
                            const actualIdx = idx + 1; // offset by 1 since wesliced from index 1
                            const handleId = resolveTargetHandle(liveNode, input.id, actualIdx);
                            return matchesTargetInput(edge, liveNode.id, handleId, actualIdx);
                        });

                        return !isTargetingRemovedInput;
                    });
                    
                    if (newEdges.length !== edges.length) {
                        onEdgeUpdate(newEdges);
                    }
                }

            } else if (op !== 'not' && inputs.length === 1) {
                updates.inputs = [...inputs, { type: 'link', offset: 0, id: `input_${uuidv4()}` }];
            }
            
            updateNodeData(liveNode.id, updates);
        };

        const handleInputChange = (idx, field, val) => {
            const newInputs = [...inputs];
            if (field === 'offset') val = Math.max(0, parseInt(val) || 0);
            newInputs[idx] = { ...newInputs[idx], [field]: val };
            updateNodeData(selectedNode.id, { inputs: newInputs });
        };

        const addInput = () => {
             // Generate UUID for new input
            const newInputs = [...inputs, { type: 'link', offset: 0, id: `input_${uuidv4()}` }];
            updateNodeData(selectedNode.id, { inputs: newInputs });
        };

        const removeInput = (idx) => {
            if (inputs.length <= (isUnary ? 1 : 2)) return;
            const removedInput = inputs[idx] || {};
            const removedHandleId = resolveTargetHandle(liveNode, removedInput.id, idx);
            const newInputs = inputs.filter((_, i) => i !== idx);
            updateNodeData(selectedNode.id, { inputs: newInputs });

            if (onEdgeUpdate) {
                const newEdges = edges.filter(ed => !matchesTargetInput(ed, liveNode.id, removedHandleId, idx));
                onEdgeUpdate(newEdges);
            }
        };

        return (
             <div className="space-y-6 animate-fadeIn">
                {renderNodeInfoSection('Logic Operation', 'Logic Gate')}
                
                {/* Operator Selector */}
                <div>
                   <label className="block text-[10px] font-bold text-fg-3 uppercase tracking-widest mb-2">
                        LOGIC OPERATION
                    </label>
                    <div className="flex rounded-xl overflow-hidden border border-[var(--divider)]">
                        {['AND', 'OR', 'NOT'].map(op => (
                            <button
                                key={op}
                                onClick={() => handleOpChange(op)}
                                className={`flex-1 py-3 text-xs font-bold uppercase tracking-wide transition-all ${
                                    (nodeData.operator || 'and') === op.toLowerCase()
                                        ? 'bg-[var(--node-math)] text-white'
                                        : 'bg-bg-3 dark:bg-bg-2 text-fg-3 hover:text-fg-2'
                                }`}
                            >
                                {op}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Inputs Configuration */}
                <div className="space-y-4">
                     <label className="block text-[10px] font-bold text-fg-3 uppercase tracking-widest flex justify-between items-center px-1">
                        CONDITIONS
                        {!isUnary && (
                            <button 
                                onClick={addInput}
                                className="text-[var(--node-math)] hover:text-[var(--node-math)] text-[9px] flex items-center gap-1 font-black"
                            >
                                <FiPlus size={10} /> ADD CONDITION
                            </button>
                        )}
                    </label>

                    <div className="space-y-3">
                        {inputs.map((input, idx) => {
                            const handleId = resolveTargetHandle(liveNode, input.id, idx);
                            // Robust Edge Lookup: Check both possible handle IDs (UUID or index-based)
                            const edge = edges.find(e => matchesTargetInput(e, liveNode.id, handleId, idx));
                            
                            const currentSourceId = edge ? edge.source : (input.nodeId || '');
                            const currentField = edge ? (edge.sourceHandle || 'value') : (input.field || 'value');
                            
                            const sourceNode = nodes.find(n => n.id === currentSourceId);
                            const fields = resolveSourceFields(sourceNode, availableFields, allIndicatorMetadata);

                            return (
                                <div key={input.id || idx} className="bg-bg-3 dark:bg-bg-2/50 p-3 rounded-xl border border-[var(--divider)] relative group animate-slideInRight">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-[10px] font-black text-fg-3 uppercase tracking-tighter">Condition {idx + 1}</span>
                                        
                                        {!isUnary && inputs.length > 2 && (
                                            <button 
                                                onClick={() => removeInput(idx)}
                                                className="p-1 text-fg-3 hover:text-[var(--color-loss)] transition-colors opacity-0 group-hover:opacity-100"
                                            >
                                                <FiTrash2 size={12} />
                                            </button>
                                        )}
                                    </div>

                                    <div className="space-y-3">
                                        <div className="grid grid-cols-5 gap-2">
                                            <div className="col-span-3">
                                                <label className="block text-[8px] font-bold text-fg-3 uppercase mb-1">Source Node</label>
                                                <select
                                                    className="w-full px-2 py-1.5 rounded bg-bg-2 border border-[var(--border)] text-[11px] font-bold outline-none focus:border-[var(--node-math)] truncate"
                                                    value={currentSourceId}
                                                    onChange={(e) => {
                                                        const newSourceId = e.target.value;
                                                        const newSourceNode = nodes.find(n => n.id === newSourceId);
                                                        const resolvedFields = resolveSourceFields(newSourceNode, availableFields, allIndicatorMetadata);
                                                        const defaultField = resolvedFields.includes('close') ? 'close' : (resolvedFields[0] || 'value');
                                                        commitOperandLink({
                                                            inputs,
                                                            inputIndex: idx,
                                                            targetHandle: handleId,
                                                            sourceId: newSourceId,
                                                            field: defaultField,
                                                            edgeStyle: { strokeWidth: 2, stroke: 'var(--node-math)' },
                                                        });
                                                    }}
                                                >
                                                    <option value="">Select Source...</option>
                                                    {availableSources
                                                        .filter(src => [...LOGIC_NODE_TYPES, 'indicator', 'math'].includes(src.type))
                                                        .map(opt => (
                                                        <option key={opt.id} value={opt.id}>
                                                            {/* Use the pre-calculated smart label from availableSources */}
                                                            {['comparator', 'logic_gate'].includes(opt.type) ? (opt.type === 'comparator' ? '[COMP] ' : '[LOGIC] ') : ''}
                                                            {opt.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div className="col-span-2">
                                                <label className="block text-[8px] font-bold text-fg-3 uppercase mb-1">Lookback</label>
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        max="100"
                                                        value={input.offset || 0}
                                                        onChange={(e) => handleInputChange(idx, 'offset', e.target.value)}
                                                        className="w-full px-2 py-1.5 rounded bg-bg-2 border border-[var(--border)] text-[11px] font-mono font-bold outline-none focus:border-[var(--node-math)]"
                                                    />
                                                    <span className="text-[10px] font-bold text-fg-3">P</span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Field Selector for Logic Sources */}
                                        {currentSourceId && fields.length > 0 && (
                                            <div className="animate-fadeIn">
                                                <label className="block text-[8px] font-bold text-fg-3 uppercase mb-1">Target Field / Signal</label>
                                                {fields.length <= 1 ? (
                                                    <div className="px-2 py-1.5 rounded bg-bg-3 dark:bg-bg-3/50 border border-[var(--divider)] text-[10px] font-mono text-fg-3 flex items-center justify-between">
                                                        <span className="truncate">{(fields[0] || 'VALUE').toUpperCase()}</span>
                                                        <span className="text-[7px] uppercase bg-bg-3 dark:bg-bg-3 px-1 rounded ml-2">Auto</span>
                                                    </div>
                                                ) : (
                                                    <select
                                                        className="w-full px-2 py-1.5 rounded bg-bg-2 border border-[var(--border)] text-[10px] font-mono font-bold outline-none focus:border-[var(--node-math)]"
                                                        value={currentField}
                                                        onChange={(e) => {
                                                            const newField = e.target.value;
                                                            commitOperandField({
                                                                inputs,
                                                                inputIndex: idx,
                                                                targetHandle: handleId,
                                                                field: newField,
                                                            });
                                                        }}
                                                    >
                                                        {fields.map(f => <option key={f} value={f}>{f.toUpperCase()}</option>)}
                                                    </select>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
             </div>
        );
    };

    // --> Time Window Panel (#140/#141 E5) — boolean "now is inside [from, to)" session-time
    //    gate. `mode` is a builder wiring hint only; the backend GraphEvaluator never branches on
    //    it (02-fix-design.md §E4). Each bound is either a literal "HH:MM" exchange-local time or
    //    a session-relative anchor {anchor, offset_minutes} — toggle between the two forms per bound.
    const renderTimeWindowPanel = () => {
        const mode = nodeData.mode || 'entry';
        const from = nodeData.from ?? '09:30';
        const to = nodeData.to ?? { anchor: 'square_off', offset_minutes: -15 };

        const isAnchorBound = (bound) => bound !== null && typeof bound === 'object';

        const handleModeChange = (newMode) => {
            updateNodeData(selectedNode.id, { mode: newMode });
        };

        const handleBoundChange = (boundKey, newBound) => {
            updateNodeData(selectedNode.id, { [boundKey]: newBound });
        };

        const renderBoundEditor = (boundKey, label, bound) => {
            const anchored = isAnchorBound(bound);
            return (
                <div className="bg-bg-3 dark:bg-bg-2/50 p-3 rounded-xl border border-[var(--divider)]">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-bold text-fg-3">{label}</span>
                        {/* Type Toggle — mirrors the comparator LINK/NUM toggle above */}
                        <div className="flex bg-bg-3 dark:bg-bg-3 rounded p-[2px]">
                            <button
                                onClick={() => handleBoundChange(boundKey, typeof bound === 'string' ? bound : '09:30')}
                                className={`px-2 py-0.5 rounded text-[9px] font-bold transition-all ${
                                    !anchored ? 'bg-bg-3 text-[var(--node-logic)] shadow-sm' : 'text-fg-3'
                                }`}
                            >
                                TIME
                            </button>
                            <button
                                onClick={() => handleBoundChange(boundKey, anchored ? bound : { anchor: 'square_off', offset_minutes: 0 })}
                                className={`px-2 py-0.5 rounded text-[9px] font-bold transition-all ${
                                    anchored ? 'bg-bg-3 text-[var(--node-logic)] shadow-sm' : 'text-fg-3'
                                }`}
                            >
                                ANCHOR
                            </button>
                        </div>
                    </div>
                    {anchored ? (
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <label className="block text-[8px] font-bold text-fg-3 uppercase mb-1">Anchor</label>
                                <select
                                    className="w-full px-2 py-1.5 rounded bg-bg-2 border border-[var(--border)] text-xs font-bold outline-none focus:border-[var(--node-logic)]"
                                    value={bound.anchor || 'square_off'}
                                    onChange={(e) => handleBoundChange(boundKey, { ...bound, anchor: e.target.value })}
                                >
                                    <option value="open">Session Open</option>
                                    <option value="close">Session Close</option>
                                    <option value="square_off">Square-off</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-[8px] font-bold text-fg-3 uppercase mb-1">Offset (min)</label>
                                <input
                                    type="number"
                                    value={bound.offset_minutes ?? 0}
                                    onChange={(e) => handleBoundChange(boundKey, { ...bound, offset_minutes: parseInt(e.target.value, 10) || 0 })}
                                    className="w-full px-2 py-1.5 rounded bg-bg-2 border border-[var(--border)] text-xs font-mono font-bold outline-none focus:border-[var(--node-logic)]"
                                />
                            </div>
                        </div>
                    ) : (
                        <div>
                            <label className="block text-[8px] font-bold text-fg-3 uppercase mb-1">Exchange Local Time (HH:MM)</label>
                            <input
                                type="time"
                                value={typeof bound === 'string' ? bound : '09:30'}
                                onChange={(e) => handleBoundChange(boundKey, e.target.value)}
                                className="w-full px-2 py-1.5 rounded bg-bg-2 border border-[var(--border)] text-xs font-mono font-bold outline-none focus:border-[var(--node-logic)]"
                            />
                        </div>
                    )}
                </div>
            );
        };

        return (
            <div className="space-y-6 animate-fadeIn">
                {renderNodeInfoSection('Session Time Gate', 'Time Window')}

                <div>
                    <label className="block text-[10px] font-bold text-fg-3 uppercase tracking-widest mb-2">
                        MODE
                    </label>
                    <div className="flex rounded-xl overflow-hidden border border-[var(--divider)]">
                        {['entry', 'exit', 'both'].map(m => (
                            <button
                                key={m}
                                onClick={() => handleModeChange(m)}
                                className={`flex-1 py-3 text-xs font-bold uppercase tracking-wide transition-all ${
                                    mode === m
                                        ? 'bg-[var(--node-logic)] text-white'
                                        : 'bg-bg-3 dark:bg-bg-2 text-fg-3 hover:text-fg-2'
                                }`}
                            >
                                {m}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="space-y-4">
                    {renderBoundEditor('from', 'From', from)}
                    {renderBoundEditor('to', 'To', to)}
                </div>
            </div>
        );
    };


    // --> Candlestick/Heikin Panel
    const renderCandlePanel = () => (
        <div className="space-y-6">
             {renderNodeInfoSection('OHLC Data', 'Candlestick Visual')}
        </div>
    );

    // --> Line Chart Panel
    const renderLinePanel = () => {
        const fields = availableFields;
        return (
            <div className="space-y-4">
                {renderNodeInfoSection('Data Stream', 'Line Visual')}
                <div>
                    <label className="block text-xs font-bold text-fg-3 uppercase tracking-wider mb-2">Source Data Field</label>
                    <select 
                        className="w-full p-2 rounded-lg bg-bg-3 dark:bg-bg-2 border border-[var(--divider)] text-sm font-bold outline-none"
                        value={nodeData.field || 'CLOSE'}
                        onChange={(e) => updateNodeData(selectedNode.id, { field: e.target.value })}
                    >
                        <optgroup label="Standard Fields">
                            {fields.standard.map(f => (
                                <option key={f} value={f}>{f}</option>
                            ))}
                        </optgroup>
                        <optgroup label="Calculated Fields">
                            {fields.calculated.map(f => (
                                <option key={f} value={f}>{f}</option>
                            ))}
                        </optgroup>
                    </select>
                    <p className="text-[10px] text-fg-3 mt-1">
                        Chart will use available data fields. Missing fields return undefined.
                    </p>
                </div>
            </div>
        );
    };

    // --> Area Chart Panel
    const renderAreaPanel = () => {
        const fields = availableFields;
        return (
            <div className="space-y-4">
                 {renderNodeInfoSection('Data Stream', 'Area Visual')}
                <div>
                    <label className="block text-xs font-bold text-fg-3 uppercase tracking-wider mb-2">Source Data Field</label>
                    <select 
                        className="w-full p-2 rounded-lg bg-bg-3 dark:bg-bg-2 border border-[var(--divider)] text-sm font-bold outline-none"
                        value={nodeData.field || 'CLOSE'}
                        onChange={(e) => updateNodeData(selectedNode.id, { field: e.target.value })}
                    >
                         <optgroup label="Standard Fields">
                            {fields.standard.map(f => (
                                <option key={f} value={f}>{f}</option>
                            ))}
                        </optgroup>
                        <optgroup label="Calculated Fields">
                            {fields.calculated.map(f => (
                                <option key={f} value={f}>{f}</option>
                            ))}
                        </optgroup>
                    </select>
                    <p className="text-[10px] text-fg-3 mt-1">
                        Chart will use available data fields. Missing fields return undefined.
                    </p>
                </div>
            </div>
        );
    };

    // --> Indicator Panel (Redesigned)
    const renderIndicatorPanel = () => {
        const params = nodeData.params || {};
        const entries = Object.entries(params).filter(([key]) => !['source', 'timeframe'].includes(key));
        //* slice 5: registry parameter metadata (description/default/range) for the "?" help
        const paramMeta = (allIndicatorMetadata || []).find(m => m.name === nodeData.indicator)?.params || [];
        //! Surface slope_mode + vol_period on dee_trend_slope nodes that predate the params (older
        //!  saved graphs lack these keys). Inject defaults so the controls render; the first onChange
        //!  writes them into the node's params. New nodes already carry them from the config.
        if (nodeData.indicator === 'dee_trend_slope') {
            if (!entries.some(([k]) => k === 'slope_mode')) {
                entries.push(['slope_mode', params.slope_mode ?? 'raw']);
            }
            if (!entries.some(([k]) => k === 'vol_period')) {
                entries.push(['vol_period', params.vol_period ?? 50]);
            }
        }
        
        const sourceOptions = nodes
            .filter(n => (n.type === 'indicator' && n.id !== selectedNode.id) || n.type === 'data_source')
            .map(n => ({
                id: n.id,
                label: n.data.label || n.data.name || (n.type === 'data_source' ? 'Market Data' : 'Indicator'),
                type: n.type,
                timeframe: n.data.timeframe
            }));
            
        const marketNode = nodes.find(n => n.type === 'data_source');
        const defaultSourceId = marketNode ? marketNode.id : 'default';
        const selectedSourceId = nodeData.sourceId === 'default' || !nodeData.sourceId ? defaultSourceId : nodeData.sourceId;
        const selectedSourceNode = nodes.find(n => n.id === selectedSourceId);
        const isMarketSource = selectedSourceNode?.type === 'data_source';

        const showSourceField = (() => {
             const inputs = nodeData.inputs || [];
             if (inputs && inputs.length > 0) return inputs.some(i => ['source', 'real', 'inReal', 'price'].includes(i));
             if ('source' in params) return true;
             return false;
        })();

        const handleSourceFieldChange = (newField) => {
            const updates = { sourceField: newField, params: { ...params, source: newField.toLowerCase() } };
            updateNodeData(selectedNode.id, updates);
        };
        

        return (
            <div className="space-y-6 animate-fadeIn">
                {/* Configuration Section */}
                <div className="bg-bg-3 dark:bg-bg-2/50 p-4 rounded-xl border border-[var(--divider)]">
                     <label className="block text-xs font-bold text-fg-2 uppercase tracking-wider mb-4 border-b border-[var(--divider)] pb-2 flex items-center gap-2">
                        <FiSliders className="text-[var(--color-accent)]" /> Configuration
                    </label>

                     <div className="space-y-5">
                         {/* Input source & Timeframe selection */}
                         <div className="flex flex-col sm:flex-row gap-4">
                             <div className="flex-1">
                                 <label className="block text-[10px] font-bold text-fg-3 uppercase tracking-widest mb-1.5">
                                     Input Source
                                 </label>
                                 <select
                                    className="w-full p-2.5 rounded-lg bg-bg-2 border border-[var(--divider)] text-sm font-medium outline-none truncate"
                                    value={selectedSourceId}
                                    onChange={(e) => updateNodeData(selectedNode.id, { sourceId: e.target.value })}
                                 >
                                     {sourceOptions.map(opt => (
                                         <option key={opt.id} value={opt.id}>
                                             {opt.type === 'data_source' ? `[MKT] ${opt.label}` : `[IND] ${opt.label}`}
                                         </option>
                                     ))}
                                     {sourceOptions.length === 0 && <option value="default">No Sources</option>}
                                 </select>
                             </div>

                             {isMarketSource && (
                                 <div className="flex-1 animate-fadeIn">
                                     <label className="block text-[10px] font-bold text-fg-3 uppercase tracking-widest mb-1.5">
                                         Timeframe
                                     </label>
                                     <select
                                         className="w-full p-2.5 rounded-lg bg-bg-2 border border-[var(--divider)] text-sm font-mono font-bold outline-none ring-1 ring-[color-mix(in_oklch,var(--color-accent)_25%,transparent)]"
                                         value={nodeData.timeframe || '5m'}
                                         onChange={(e) => updateNodeData(selectedNode.id, { timeframe: e.target.value })}
                                     >
                                         {TIMEFRAME_OPTIONS.map(tf => <option key={tf} value={tf}>{tf}</option>)}
                                     </select>
                                 </div>
                             )}
                         </div>
                         
                         {/* Source Field Row */}
                         {showSourceField && (
                            <div className="pt-1">
                                 <label className="block text-[10px] font-bold text-fg-3 uppercase tracking-widest mb-1.5 flex items-center gap-2">
                                     <span className="w-1 h-1 rounded-full bg-[var(--color-accent)]"></span> Source Field
                                 </label>
                                 {(() => {
                                     if (isMarketSource) {
                                         return (
                                             <select
                                                 className="w-full px-3 py-2 rounded-lg border border-[var(--divider)] bg-bg-2 font-mono text-xs font-bold outline-none"
                                                 value={nodeData.sourceField || 'CLOSE'}
                                                 onChange={(e) => handleSourceFieldChange(e.target.value)}
                                             >
                                                <optgroup label="Standard">
                                                    {availableFields.standard.map(f => <option key={f} value={f}>{f}</option>)}
                                                </optgroup>
                                                <optgroup label="Calculated">
                                                    {availableFields.calculated.map(f => <option key={f} value={f}>{f}</option>)}
                                                </optgroup>
                                             </select>
                                         );
                                     }
                                     const outputs = selectedSourceNode?.data?.outputs || ['value'];
                                     if (outputs.length <= 1) {
                                         return (
                                             <div className="px-3 py-2 rounded-lg border border-[var(--divider)] bg-bg-3 dark:bg-bg-2 text-xs font-mono text-fg-3 flex items-center justify-between">
                                                 <span>{outputs[0]?.toUpperCase() || 'VALUE'}</span>
                                                 <span className="text-[9px] uppercase bg-bg-3 dark:bg-bg-3 px-1.5 rounded">Auto</span>
                                             </div>
                                         );
                                     }
                                     return (
                                         <select
                                             className="w-full px-3 py-2 rounded-lg border border-[var(--divider)] bg-bg-2 font-mono text-xs font-bold outline-none"
                                             value={nodeData.sourceHandle || 'value'}
                                             onChange={(e) => updateNodeData(selectedNode.id, { sourceHandle: e.target.value })}
                                         >
                                            {outputs.map(f => <option key={f} value={f}>{f.toUpperCase()}</option>)}
                                         </select>
                                     );
                                 })()}
                            </div>
                         )}

                         {/* Parameters Section */}
                         {entries.length > 0 && (
                             <div className="pt-2 border-t border-[var(--divider)]">
                                <label className="block text-[10px] font-bold text-fg-3 uppercase tracking-widest mb-3">
                                    Parameters
                                </label>
                                <div className="space-y-4">
                                    {entries.map(([key, value]) => {
                                        const _cls = "w-full px-3 py-2.5 rounded-lg border border-[var(--divider)] bg-bg-2 font-mono text-sm font-medium focus:ring-2 focus:ring-[color-mix(in_oklch,var(--color-accent)_50%,transparent)] outline-none";
                                        // String/enum params (e.g. dee_trend_slope.slope_mode) must NOT go through the number
                                        // input (parseFloat -> NaN). Known enums render as a dropdown; other strings as text.
                                        const ENUM_OPTIONS = {
                                            slope_mode: [
                                                { value: 'raw', label: 'Absolute (raw — within-instrument)' },
                                                { value: 'vol_norm', label: 'Trend angle (σ-scaled, 45°=1σ/bar)' },
                                            ],
                                        };
                                        const opts = ENUM_OPTIONS[key];
                                        const isString = typeof value === 'string';
                                        const isModelVersion = nodeData.indicator === 'model_regime' && key === 'model_version_id';
                                        const pMeta = paramMeta.find(pm => pm.name === key);
                                        const help = hasParamHelp(pMeta);
                                        const helpOpen = help && paramHelp.nodeId === selectedNode.id && paramHelp.key === key;
                                        const helpId = `param-help-body-${selectedNode.id}-${key}`;
                                        const pDefault = fmtParamValue(pMeta?.default);
                                        const pMin = fmtParamValue(pMeta?.min_value);
                                        const pMax = fmtParamValue(pMeta?.max_value);
                                        return (
                                            <div key={key}>
                                                <div className="flex justify-between items-center mb-1">
                                                    <span className="text-[10px] font-bold text-fg-3 uppercase">{key.replace(/_/g, ' ')}</span>
                                                    {help && (
                                                        <button
                                                            type="button"
                                                            data-testid={`param-help-${key}`}
                                                            aria-label={`About ${key.replace(/_/g, ' ')}`}
                                                            aria-expanded={helpOpen}
                                                            aria-controls={helpId}
                                                            onClick={() => setParamHelp(helpOpen ? { nodeId: null, key: null } : { nodeId: selectedNode.id, key })}
                                                            className={`w-4 h-4 rounded-full border text-[9px] font-bold leading-none flex items-center justify-center transition-colors ${helpOpen ? 'border-[var(--color-accent)] text-[var(--color-accent)]' : 'border-[var(--divider)] text-fg-3 hover:text-fg-1'}`}
                                                        >?</button>
                                                    )}
                                                </div>
                                                {helpOpen && (
                                                    <div id={helpId} role="region" data-testid={`param-help-body-${key}`} className="mb-2 p-2 rounded-lg bg-bg-2 border border-[var(--divider)] text-[11px] text-fg-2 space-y-1">
                                                        <div>{pMeta.description}</div>
                                                        {(pDefault !== null || pMin !== null || pMax !== null) && (
                                                            <div className="font-mono text-[10px] text-fg-3">
                                                                {pDefault !== null && <span>default {pDefault}</span>}
                                                                {(pMin !== null || pMax !== null) && <span>{pDefault !== null ? ' · ' : ''}range {pMin ?? '…'} to {pMax ?? '…'}</span>}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                                {isModelVersion ? (
                                                    <ModelVersionSelect
                                                        //! keyed per node: a node switch remounts + refetches with that node's saved id (R7-T2/T4)
                                                        key={`model-version-${selectedNode.id}`}
                                                        value={value}
                                                        instrumentId={strategyScope.instrumentId}
                                                        instrumentLabel={strategyScope.instrumentLabel}
                                                        timeframe={strategyScope.timeframe}
                                                        onChange={(modelVersionId, option = {}) => updateNodeData(selectedNode.id, {
                                                            params: {
                                                                ...params,
                                                                [key]: modelVersionId,
                                                                frame_set: option.frame_set || option.binding?.frame_set || [],
                                                                feature_schema_hash: option.feature_schema_hash || option.binding?.feature_schema_hash || null,
                                                                head: option.head || option.binding?.head || null,
                                                                output_schema_version: option.output_schema_version || option.binding?.output_schema_version || null,
                                                            },
                                                            outputs: option.output_names || option.outputs
                                                                || option.binding?.output_names || option.binding?.outputs || [],
                                                        })}
                                                    />
                                                ) : opts ? (
                                                    <select value={value} onChange={(e) => updateNodeData(selectedNode.id, { params: { ...params, [key]: e.target.value } })} className={_cls}>
                                                        {opts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                                    </select>
                                                ) : isString ? (
                                                    <input type="text" value={value} onChange={(e) => updateNodeData(selectedNode.id, { params: { ...params, [key]: e.target.value } })} className={_cls} />
                                                ) : (
                                                    <input type="number" value={value} onChange={(e) => { const newVal = parseFloat(e.target.value); updateNodeData(selectedNode.id, { params: { ...params, [key]: isNaN(newVal) ? 0 : newVal } }); }} className={_cls} />
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                             </div>
                         )}
                     </div>
                </div>

                {/* Remove Button */}
                <div className="flex justify-center mt-6 pt-4 border-t border-[var(--divider)]">
                    <button 
                        onClick={() => onDeleteNode && onDeleteNode(selectedNode.id)}
                        className="text-xs font-bold text-[var(--color-loss)] flex items-center gap-2 hover:bg-[color-mix(in_oklch,var(--color-loss)_10%,transparent)] dark:hover:bg-[color-mix(in_oklch,var(--color-loss)_15%,transparent)] px-4 py-2 rounded-lg transition-colors"
                    >
                        <FiTrash2 /> Remove Indicator
                    </button>
                </div>
            </div>
        );
    };



    // --> Arithmetic/Math Panel
    const renderMathPanel = () => {
        const mathOperators = [
            { value: 'add', label: 'Add', symbol: '+' },
            { value: 'sub', label: 'Subtract', symbol: '-' },
            { value: 'mul', label: 'Multiply', symbol: '×' },
            { value: 'div', label: 'Divide', symbol: '÷' }
        ];

        const inputs = nodeData.inputs || [
            { type: 'link', offset: 0, id: '0' },
            { type: 'link', offset: 0, id: '1' }
        ];

        const handleOpChange = (newOp) => {
            updateNodeData(selectedNode.id, { operator: newOp });
        };

        const handleInputChange = (idx, field, val) => {
            const newInputs = [...inputs];
            if (field === 'offset') val = Math.max(0, parseInt(val) || 0);
            newInputs[idx] = { ...newInputs[idx], [field]: val };
            updateNodeData(selectedNode.id, { inputs: newInputs });
        };

        const addInput = () => {
            const newInputs = [...inputs, { type: 'link', offset: 0, id: `input_${uuidv4()}` }];
            updateNodeData(selectedNode.id, { inputs: newInputs });
        };

        const removeInput = (idx) => {
            if (inputs.length <= 2) return;
            const removedInput = inputs[idx] || {};
            const removedHandleId = resolveTargetHandle(liveNode, removedInput.id, idx);
            const newInputs = inputs.filter((_, i) => i !== idx);
            updateNodeData(selectedNode.id, { inputs: newInputs });

            if (onEdgeUpdate) {
                const newEdges = edges.filter(ed => !matchesTargetInput(ed, liveNode.id, removedHandleId, idx));
                onEdgeUpdate(newEdges);
            }
        };

        return (
            <div className="space-y-6 animate-fadeIn">
                {renderNodeInfoSection('Mathematical Ops', 'Arithmetic Node')}
                
                {/* Operator Selector */}
                <div>
                    <label className="block text-[10px] font-bold text-fg-3 uppercase tracking-widest mb-2">
                        OPERATOR
                    </label>
                    <div className="flex rounded-xl overflow-hidden border border-[var(--divider)]">
                        {mathOperators.map(op => (
                            <Tooltip key={op.value} content={op.label} placement="top">
                            <button
                                onClick={() => handleOpChange(op.value)}
                                className={`flex-1 py-3 text-xs font-bold uppercase tracking-wide transition-all ${
                                    (nodeData.operator || 'add') === op.value
                                        ? 'bg-fg-1 text-bg-1'
                                        : 'bg-bg-2 text-fg-3 hover:text-fg-2 hover:bg-bg-3'
                                }`}
                                aria-label={op.label}
                            >
                                {op.symbol}
                            </button>
                            </Tooltip>
                        ))}
                    </div>
                </div>

                {/* Inputs Configuration */}
                <div>
                     <label className="block text-[10px] font-bold text-fg-3 uppercase tracking-widest mb-3 flex justify-between items-center">
                        INPUTS CONFIGURATION
                        <button 
                            onClick={addInput}
                            className="text-[var(--color-accent)] hover:text-[var(--color-accent)] text-[9px] flex items-center gap-1"
                        >
                            <FiX className="rotate-45" /> ADD INPUT
                        </button>
                    </label>
                    
                    <div className="space-y-3">
                        {inputs.map((input, idx) => {
                            const handleId = resolveTargetHandle(liveNode, input.id, idx);
                            // Robust Edge Lookup: Check both possible handle IDs (UUID or index-based)
                            const edge = edges.find(e => matchesTargetInput(e, liveNode.id, handleId, idx));
                            
                            const currentSourceId = edge ? edge.source : (input.nodeId || '');
                            const currentField = edge ? (edge.sourceHandle || 'value') : (input.field || 'value');
                            
                            const sourceNode = nodes.find(n => n.id === currentSourceId);
                            const fields = resolveSourceFields(sourceNode, availableFields, allIndicatorMetadata);

                            return (
                                <div key={input.id || idx} className="bg-bg-3 dark:bg-bg-2/50 p-3 rounded-xl border border-[var(--divider)] relative group animate-slideInRight">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-xs font-bold text-fg-3">Input {idx + 1}</span>
                                        
                                        {/* Type Toggle */}
                                        <div className="flex bg-bg-3 dark:bg-bg-3 rounded p-[2px]">
                                            <button
                                                onClick={() => handleInputChange(idx, 'type', 'link')}
                                                className={`px-2 py-0.5 rounded text-[9px] font-bold transition-all ${
                                                    input.type === 'link' ? 'bg-bg-3 text-[var(--node-visual)] shadow-sm' : 'text-fg-3'
                                                }`}
                                            >
                                                LINK
                                            </button>
                                            <button
                                                onClick={() => {
                                                    commitOperandConstant({ inputs, inputIndex: idx, targetHandle: handleId });
                                                }}
                                                className={`px-2 py-0.5 rounded text-[9px] font-bold transition-all ${
                                                    input.type === 'constant' ? 'bg-bg-3 text-[var(--warning)] shadow-sm' : 'text-fg-3'
                                                }`}
                                            >
                                                NUM
                                            </button>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 gap-3">
                                        { input.type === 'constant' ? (
                                            <div>
                                                <label className="block text-[8px] font-bold text-fg-3 uppercase mb-1">Constant Value</label>
                                                <input
                                                    type="number"
                                                    step="any"
                                                    value={input.value || ''}
                                                    onChange={(e) => handleInputChange(idx, 'value', e.target.value)}
                                                    onBlur={(e) => handleInputChange(idx, 'value', parseFloat(e.target.value) || 0)}
                                                    className="w-full px-2 py-1.5 rounded bg-bg-2 border border-[var(--border)] text-xs font-mono font-bold outline-none focus:border-[var(--color-accent)]"
                                                    placeholder="0.0"
                                                />
                                            </div>
                                        ) : (
                                            <div className="space-y-2">
                                                {/* Source Select */}
                                                <div>
                                                    <label className="block text-[8px] font-bold text-fg-3 uppercase mb-1">Linked Source</label>
                                                    <select
                                                        className="w-full px-2 py-1.5 rounded bg-bg-2 border border-[var(--border)] text-xs font-bold outline-none focus:border-[var(--color-accent)] truncate"
                                                        value={currentSourceId}
                                                        onChange={(e) => {
                                                            const newSourceId = e.target.value;
                                                            const newSourceNode = nodes.find(n => n.id === newSourceId);
                                                            const resolvedFields = resolveSourceFields(newSourceNode, availableFields, allIndicatorMetadata);
                                                            const defaultField = resolvedFields.includes('close') ? 'close' : (resolvedFields[0] || 'value');
                                                            commitOperandLink({
                                                                inputs,
                                                                inputIndex: idx,
                                                                targetHandle: handleId,
                                                                sourceId: newSourceId,
                                                                field: defaultField,
                                                                edgeStyle: { strokeWidth: 2, stroke: 'var(--color-accent)' },
                                                            });
                                                        }}
                                                    >
                                                        <option value="">Select Source...</option>
                                                        {availableSources
                                                            .map(src => (
                                                            <option key={src.id} value={src.id}>
                                                                {src.type === 'data_source' ? '[MKT] ' : src.type === 'math' ? '[MATH] ' : src.type === 'comparator' ? '[COMP] ' : src.type === 'logic_gate' ? '[LOGIC] ' : '[IND] '}
                                                                {src.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>

                                                {/* Field Select */}
                                                {(() => {
                                                    if (!edge && !currentSourceId) return null;
                                                    if (fields.length === 0) return null;

                                                    return (
                                                        <div>
                                                            <label className="block text-[8px] font-bold text-fg-3 uppercase mb-1">Source Field</label>
                                                            
                                                            {fields.length <= 1 ? (
                                                                <div className="px-2 py-1.5 rounded bg-bg-3 dark:bg-bg-2/50 border border-[var(--divider)] text-[10px] font-mono text-fg-3 flex items-center justify-between h-[30px]">
                                                                    <span className="truncate">{(fields[0] || 'VALUE').toUpperCase()}</span>
                                                                    <span className="text-[8px] uppercase bg-bg-3 dark:bg-bg-3 px-1 rounded ml-2">Auto</span>
                                                                </div>
                                                            ) : (
                                                                 <select
                                                                    className="w-full px-2 py-1.5 rounded bg-bg-2 border border-[var(--border)] text-[10px] font-mono font-bold outline-none focus:border-[var(--color-accent)] h-[30px]"
                                                                    value={currentField}
                                                                    onChange={(e) => {
                                                                        const newField = e.target.value;
                                                                        commitOperandField({
                                                                            inputs,
                                                                            inputIndex: idx,
                                                                            targetHandle: handleId,
                                                                            field: newField,
                                                                        });
                                                                    }}
                                                                >
                                                                    {fields.map((f, i) => (
                                                                        <option key={`${f}_${i}`} value={f}>{f.toUpperCase()}</option>
                                                                    ))}
                                                                </select>
                                                            )}
                                                        </div>
                                                    );
                                                })()}

                                                {/* Lookback Input (Only for Links) */}
                                                <div>
                                                    <label className="block text-[8px] font-bold text-fg-3 uppercase mb-1">Lookback (Bars)</label>
                                                    <div className="relative">
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            value={input.offset || 0}
                                                            onChange={(e) => handleInputChange(idx, 'offset', e.target.value)}
                                                            className="w-full px-2 py-1.5 rounded bg-bg-2 border border-[var(--border)] text-xs font-mono font-bold outline-none focus:border-[var(--color-accent)] h-[30px]"
                                                        />
                                                        <span className={`absolute right-2 top-1.5 text-[8px] font-bold uppercase pointer-events-none ${input.offset > 0 ? 'text-[var(--warning)]' : 'text-fg-3'}`}>
                                                            {input.offset > 0 ? 'PREV' : 'CURR'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Remove Button */}
                                    {inputs.length > 2 && (
                                        <Tooltip content="Remove Input" placement="top">
                                        <button
                                            onClick={() => removeInput(idx)}
                                            className="absolute -top-2 -right-2 bg-bg-2 text-[var(--color-loss)] p-1 rounded-full shadow border border-[var(--divider)] opacity-0 group-hover:opacity-100 transition-opacity"
                                            aria-label="Remove Input"
                                        >
                                            <FiTrash2 size={10} />
                                        </button>
                                        </Tooltip>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    };

    // --> Signal Panel (Execution)
    const renderSignalPanel = () => {
        const input = (nodeData.inputs && nodeData.inputs[0]) || { type: 'link', offset: 0, id: 'input_0' };
        const handleId = input.id || 'input_0';
        
        const edge = edges.find(e => matchesTargetInput(e, liveNode.id, handleId, 0));
        
        const currentSourceId = edge ? edge.source : (input.nodeId || '');
        const currentField = edge ? (edge.sourceHandle || 'value') : (input.field || 'value');
        
        const sourceNode = nodes.find(n => n.id === currentSourceId);
        const fields = resolveSourceFields(sourceNode, availableFields, allIndicatorMetadata);

        const handleInputChange = (field, val) => {
            const newInputs = [{ ...input, [field]: val }];
            updateNodeData(selectedNode.id, { inputs: newInputs });
        };
        
        const handleParamChange = (key, val) => {
            updateNodeData(selectedNode.id, { 
                params: { ...(nodeData.params || {}), [key]: val } 
            });
        };

        const action = nodeData.action?.replace('_', ' ').toUpperCase() || 'SIGNAL';
        const rawAction = nodeData.action || '';
        const positionType = nodeData.type?.toUpperCase() || '';
        const currentExecutionStrategy = nodeData.execution_strategy || 'single';
        const currentStage = nodeData.stage || 1;

        const attemptAutoWire = (mode, targetStage) => {
            if (!['sl', 'tp'].includes(rawAction)) return null;

            // CUSTOM mode: signal handle (input_0) still auto-wires to the entry node so the
            // entry association is always visible. The amber trigger_input handle is NEVER
            // touched here — it is left exclusively for the user to wire manually to a
            // Comparator or Logic Gate. The edges filter below only removes input_0-style edges
            // (normalised to '0'), so any existing trigger_input edge is always preserved.

            const isRiskLong = positionType === 'LONG' || positionType === 'LONG RISK';
            const isRiskShort = positionType === 'SHORT' || positionType === 'SHORT RISK';

            const entryNode = findAssociatedEntryNode(nodes, liveNode.id, targetStage, isRiskLong, isRiskShort);

            const associationEdges = mode ? edges.filter(ed => (
                ed.target === liveNode.id && ed.targetHandle !== 'trigger_input'
            )) : [];
            const removeEdgeIds = associationEdges.map(({ id }) => id);

            if (entryNode) {
                const outcome = graphWiring.onConnect({
                    source: entryNode.id,
                    sourceHandle: 'value',
                    target: liveNode.id,
                    targetHandle: handleId,
                }, {
                    removeEdgeIds,
                    edgeStyle: {
                        strokeWidth: 2, 
                        stroke: rawAction === 'sl' ? 'var(--color-loss)' : 'var(--color-profit)',
                        strokeDasharray: '5,5',
                    },
                });
                setLinkRefusal(outcome.ok ? '' : outcome.reason);
                return outcome;
            }
            if (removeEdgeIds.length > 0) {
                const outcome = graphWiring.handleEdgesChange(
                    removeEdgeIds.map(id => ({ type: 'remove', id }))
                );
                setLinkRefusal(outcome.ok ? '' : outcome.reason);
                return outcome;
            }
            setLinkRefusal('');
            return { ok: true, reason: '' };
        };

        // Action Display Header Helper
        const renderActionDisplay = () => {
            let displayAction = action;
            let IconComponent = FiActivity;
            let themeBase = 'bg-[color-mix(in_oklch,var(--node-comparator)_12%,transparent)] text-[var(--node-comparator)]';

            if (rawAction === 'sl') {
                displayAction = `STOP LOSS${positionType ? ` (${positionType})` : ''}`;
                IconComponent = FiShield;
                themeBase = 'bg-[color-mix(in_oklch,var(--color-loss)_12%,transparent)] text-[var(--color-loss)]';
            } else if (rawAction === 'tp') {
                displayAction = `TAKE PROFIT${positionType ? ` (${positionType})` : ''}`;
                IconComponent = FiTarget;
                themeBase = 'bg-[color-mix(in_oklch,var(--color-profit)_12%,transparent)] text-[var(--color-profit)]';
            } else if (rawAction === 'time_exit') {
                displayAction = 'TIME EXIT';
                IconComponent = FiClock;
                themeBase = 'bg-[color-mix(in_oklch,var(--warning)_12%,transparent)] text-[var(--warning)]';
            } else if (action === 'ENTER LONG' || action === 'ENTER SHORT' || action === 'EXIT LONG' || action === 'EXIT SHORT') {
                displayAction = action; // use unmodified action for standard
            }

            return (
                <div className="bg-bg-3 dark:bg-bg-1/40 p-3 rounded-lg flex items-center gap-3 border border-[var(--divider)] mb-4">
                    <div className={`w-10 h-10 rounded-full ${themeBase} flex items-center justify-center`}>
                        <IconComponent size={20} />
                    </div>
                    <div>
                        <div className="text-[10px] font-bold text-fg-3 uppercase tracking-widest">ACTION TYPE</div>
                        <div className="text-sm font-black text-fg-2 uppercase tracking-wider">{displayAction}</div>
                    </div>
                </div>
            );
        };

        // Custom Stage Render Block (reused across SL/TP/Signal)
        const renderExecutionStrategyToggle = (activeMode) => {
            const countSimilarNodes = () => {
                if (!selectedNode) return 0;
                const positionType = (nodeData.type || '').toUpperCase();
                const isRiskLong = positionType === 'LONG' || positionType === 'LONG RISK';
                const isRiskShort = positionType === 'SHORT' || positionType === 'SHORT RISK';
                
                return nodes.filter(n => {
                    if (n.type !== 'signal' && n.type !== 'position_trigger') return false;
                    if (n.data?.action !== rawAction) return false;
                    
                    if (['sl', 'tp'].includes(rawAction)) {
                        const nType = (n.data?.type || '').toUpperCase();
                        const nIsRiskLong = nType === 'LONG' || nType === 'LONG RISK';
                        const nIsRiskShort = nType === 'SHORT' || nType === 'SHORT RISK';
                        if (isRiskLong && !nIsRiskLong) return false;
                        if (isRiskShort && !nIsRiskShort) return false;
                    }
                    return true;
                }).length;
            };

            const similarCount = countSimilarNodes();
            const canMakeSingle = similarCount <= 1;

            return (
            <div className="bg-bg-3 dark:bg-bg-2/50 p-3 rounded-lg border border-[var(--divider)] mb-4">
                <label className="flex items-center text-[10px] font-bold text-fg-3 uppercase tracking-widest mb-2">
                    Execution Strategy 
                    {!canMakeSingle && <span className="ml-2 lowercase normal-case text-[var(--color-loss)] bg-[color-mix(in_oklch,var(--color-loss)_12%,transparent)] px-1 rounded" title="Multiple nodes exist for this action.">(Cannot revert to Single)</span>}
                </label>
                <div className="flex bg-bg-1 rounded p-[2px] border border-[var(--divider)]">
                    <button
                        disabled={!canMakeSingle}
                        title={!canMakeSingle ? "Delete other stages first to revert to single." : ""}
                        onClick={() => {
                            const update = { execution_strategy: 'single', stage: 1 };
                            if (activeMode) {
                                const wiring = attemptAutoWire(activeMode, 1);
                                if (wiring && !wiring.ok) return;
                            }
                            updateNodeData(selectedNode.id, update);
                        }}
                        className={`flex-1 py-1 px-2 rounded text-[10px] font-bold transition-all ${!canMakeSingle ? 'opacity-50 cursor-not-allowed' : ''} ${
                            currentExecutionStrategy === 'single'
                                ? 'bg-[var(--node-comparator)] text-white shadow-sm'
                                : 'text-fg-3 hover:text-fg-1 dark:hover:text-fg-3'
                        }`}
                    >
                        SINGLE
                    </button>
                    <button
                        onClick={() => {
                            const update = { execution_strategy: 'multi_stage', stage: currentStage };
                            if (activeMode) {
                                const wiring = attemptAutoWire(activeMode, currentStage);
                                if (wiring && !wiring.ok) return;
                            }
                            updateNodeData(selectedNode.id, update);
                        }}
                        className={`flex-1 flex items-center justify-center gap-1 py-1 px-2 rounded text-[10px] font-bold transition-all ${
                            currentExecutionStrategy === 'multi_stage'
                                ? 'bg-[var(--node-comparator)] text-white shadow-sm'
                                : 'text-fg-3 hover:text-fg-1 dark:hover:text-fg-3'
                        }`}
                    >
                        <FiLayers size={10} /> MULTI-STAGE
                    </button>
                </div>
                
                {currentExecutionStrategy === 'multi_stage' && (
                    <div className="mt-3 flex items-center justify-between border-t border-[var(--divider)] pt-3">
                        <div>
                            <span className="text-[10px] font-bold text-fg-3 uppercase">Stage Number</span>
                            <p className="text-[9px] text-fg-3 leading-tight">Order of execution.</p>
                        </div>
                        <input
                            type="number"
                            min="1"
                            max="10"
                            value={currentStage}
                            onChange={(e) => {
                                const val = parseInt(e.target.value) || 1;
                                const update = { stage: val };
                                if (activeMode) {
                                    const wiring = attemptAutoWire(activeMode, val);
                                    if (wiring && !wiring.ok) return;
                                }
                                updateNodeData(selectedNode.id, update);
                            }}
                            className="w-16 px-2 py-1 rounded bg-bg-1 border border-[var(--border)] text-xs font-mono font-bold outline-none text-center focus:border-[var(--node-comparator)]"
                        />
                    </div>
                )}
            </div>
            );
        };

        // --> MODULAR EXIT NODES CONFIGURATION
        
        const resolveActiveEntryNodes = () => {
            const activeInputs = (nodeData.inputs || []).filter(inp => inp.type === 'link');
            return activeInputs.map(inp => nodes.find(n => n.id === inp.nodeId)).filter(Boolean);
        };

        // 1. STOP LOSS NODE
        if (rawAction === 'sl') {
            // Priority: Explicit exit_mode > Trailing Flag > Standard.
            // Entry association (input_0) is always present for exit nodes and must
            // not imply CUSTOM mode by itself.
            const slMode = nodeData.params?.exit_mode || (nodeData.params?.is_trailing ? 'TRAILING' : 'STANDARD');

            // Read-only entry signals: Auto-managed dynamically via active link inputs.
            const slEntryNodes = resolveActiveEntryNodes();
            const hasSlEntries = slEntryNodes.length > 0;

            // Trigger: wired exclusively to trigger_input (user-managed in CUSTOM mode)
            const slTriggerEdge = edges.find(e =>
                e.target === liveNode.id && e.targetHandle === 'trigger_input'
            );
            const slTriggerSourceId = slTriggerEdge ? slTriggerEdge.source : '';
            const slTriggerNode = slTriggerSourceId ? nodes.find(n => n.id === slTriggerSourceId) : null;
            const slTriggerFields = slTriggerNode
                ? resolveSourceFields(slTriggerNode, availableFields, allIndicatorMetadata)
                : [];
            const slTriggerField = slTriggerEdge?.sourceHandle || slTriggerFields[0] || 'value';

            return (
                <div className="space-y-6 animate-fadeIn">
                    {renderNodeInfoSection('Risk Management', 'Logic Node')}
                    
                    {renderActionDisplay()}

                    <div className="bg-[color-mix(in_oklch,var(--color-loss)_10%,transparent)] p-4 rounded-xl border border-[color-mix(in_oklch,var(--color-loss)_30%,transparent)] space-y-4">
                        <div className="flex items-center gap-3 mb-2">
                            <FiShield className="text-[var(--color-loss)]" size={20} />
                            <h4 className="text-sm font-bold text-[var(--color-loss)]">Stop Loss Settings</h4>
                        </div>
                        
                        {renderExecutionStrategyToggle(slMode)}

                        {/* ENTRY SIGNAL — always shown, read-only, auto-wired to matching entry */}
                        <div className="rounded-lg p-2.5 bg-bg-3 dark:bg-bg-2/60 border border-[var(--divider)] flex flex-col gap-2">
                            <div className="flex items-center justify-between">
                                <div className="text-[8px] font-bold text-fg-3 uppercase tracking-widest">Entry Signal (auto-wired)</div>
                                {hasSlEntries && <span className="text-[9px] font-bold text-[var(--node-comparator)] bg-[color-mix(in_oklch,var(--node-comparator)_10%,transparent)] px-1.5 py-0.5 rounded flex-shrink-0">WIRED</span>}
                            </div>
                            
                            {hasSlEntries ? (
                                <div className="space-y-1.5">
                                    {slEntryNodes.map((n, idx) => (
                                        <div key={idx} className="flex items-center gap-2">
                                            <div className="w-2 h-2 rounded-full flex-shrink-0 bg-[var(--node-comparator)]" />
                                            <div className="text-[10px] font-bold text-fg-2 truncate flex-1 block">
                                                {n.data?.label || n.data?.action?.replace(/_/g,' ').toUpperCase() || 'Entry Node'}
                                            </div>
                                            {slEntryNodes.length > 1 && (
                                                <span className="text-[8px] font-bold text-fg-3 bg-bg-3 dark:bg-bg-3 px-1 py-0.5 rounded whitespace-nowrap">STAGE {idx + 1}</span>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="flex items-center gap-2">
                                    <div className="w-2 h-2 rounded-full flex-shrink-0 bg-bg-3" />
                                    <span className="text-[10px] text-fg-3 italic font-normal">No entry node found — add an entry first</span>
                                </div>
                            )}
                        </div>

                        {/* REFERENCE PRICE — only shown when this SL is shared across multiple entries */}
                        {slEntryNodes.length > 1 && (
                            <div className="rounded-lg p-2.5 bg-bg-3 dark:bg-bg-2/60 border border-[var(--divider)] space-y-2">
                                <div className="flex items-center gap-1.5">
                                    <label className="text-[10px] font-bold text-fg-3 uppercase tracking-widest">Reference Price</label>
                                    <div className="relative group">
                                        <span className="text-[9px] text-fg-3 cursor-help border border-[var(--border)] rounded-full w-3.5 h-3.5 inline-flex items-center justify-center font-bold">i</span>
                                        <div className="absolute left-5 top-0 z-50 hidden group-hover:block w-52 p-2 rounded-lg bg-bg-1 text-fg-1 border border-[var(--border)] text-[10px] leading-relaxed shadow-xl">
                                            {(nodeData.params?.reference_price || 'average') === 'average' && 'Uses the weighted average cost of all open stages. Industry standard.'}
                                            {nodeData.params?.reference_price === 'last' && "Uses the most recent stage's fill price."}
                                            {nodeData.params?.reference_price === 'first' && 'Uses the original entry price.'}
                                        </div>
                                    </div>
                                </div>
                                <select
                                    className="w-full px-3 py-2 rounded-lg bg-bg-2 border border-[var(--divider)] text-[10px] font-bold outline-none focus:ring-2 focus:ring-[color-mix(in_oklch,var(--color-loss)_50%,transparent)]"
                                    value={nodeData.params?.reference_price || 'average'}
                                    onChange={(e) => handleParamChange('reference_price', e.target.value)}
                                >
                                    <option value="average">Average (VWAP)</option>
                                    <option value="last">Last Entry</option>
                                    <option value="first">First Entry</option>
                                </select>
                            </div>
                        )}

                        <div>
                            <label className="block text-[10px] font-bold text-fg-3 uppercase mb-1.5">Exit Mode</label>
                            <div className="flex bg-bg-2 rounded-lg p-1 border border-[var(--divider)]">
                                {['STANDARD', 'TRAILING', 'CUSTOM'].map(mode => (
                                    <button
                                        key={mode}
                                        onClick={() => {
                                            const newParams = { 
                                                ...(nodeData.params || {}),
                                                exit_mode: mode,
                                                is_trailing: mode === 'TRAILING'
                                            };
                                            
                                            const updatePayload = { params: newParams };
                                            
                                            if (mode !== 'CUSTOM' && onEdgeUpdate) {
                                                const unlinked = linkOperandInput({
                                                    inputs: nodeData.inputs,
                                                    edges,
                                                    targetNode: liveNode,
                                                    targetId: liveNode.id,
                                                    targetHandle: 'trigger_input',
                                                    sourceId: null,
                                                    persistInput: false,
                                                });
                                                if (unlinked.edges.length !== edges.length) {
                                                    onEdgeUpdate(unlinked.edges);
                                                }
                                            }
                                            
                                            updateNodeData(selectedNode.id, updatePayload);
                                        }}
                                        className={`flex-1 py-1.5 rounded-md text-[10px] font-bold transition-all ${
                                            slMode === mode
                                                ? 'bg-[var(--color-loss)] text-white shadow-sm'
                                                : 'text-fg-3 hover:text-fg-1 dark:hover:text-fg-3'
                                        }`}
                                    >
                                        {mode}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* EXIT MODE DESCRIPTION */}
                        <div className="rounded-lg p-2.5 text-[10px] leading-relaxed bg-bg-3 dark:bg-bg-2/60 border border-[var(--divider)] text-fg-3">
                            {slMode === 'STANDARD' && <><span className="font-bold text-fg-2">Standard</span> — Self-contained. Fires automatically when price reaches the configured distance. No extra wiring needed.</>}
                            {slMode === 'TRAILING' && <><span className="font-bold text-fg-2">Trailing</span> — Stop trails price in your favour and never moves against you. Self-contained, no extra wiring needed.</>}
                            {slMode === 'CUSTOM' && <><span className="font-bold text-[var(--warning)]">Custom</span> — External trigger. Connect a <span className="font-semibold">Comparator</span> or <span className="font-semibold">Logic Gate</span> to the <span className="text-[var(--warning)] font-bold">amber Trigger handle</span>. Exit fires when that condition is TRUE.</> }
                        </div>
                        
                        {/* STANDARD / TRAILING UI */}
                        {slMode !== 'CUSTOM' && (
                            <div className="space-y-4 animate-fadeIn">
                                {/* SL Value */}
                                <div>
                                    <label className="block text-[10px] font-bold text-fg-3 uppercase mb-1.5">
                                        {slMode === 'TRAILING' ? 'Trailing Trigger Distance' : 'Stop Loss Distance'}
                                    </label>
                                    <input
                                        type="number"
                                        step="0.1"
                                        value={nodeData.params?.sl_value ?? 1.0}
                                        onChange={(e) => handleParamChange('sl_value', parseFloat(e.target.value))}
                                        className="w-full px-3 py-2 rounded-lg bg-bg-2 border border-[var(--divider)] text-sm font-bold outline-none focus:ring-2 focus:ring-[color-mix(in_oklch,var(--color-loss)_50%,transparent)]"
                                    />
                                    {slMode === 'TRAILING' && (
                                        <p className="text-[10px] text-fg-3 mt-1 italic">
                                            Distance from High/Low to maintain the stop. Moves in your favor only.
                                        </p>
                                    )}
                                </div>

                                {/* SL Type */}
                                <div>
                                    <label className="block text-[10px] font-bold text-fg-3 uppercase mb-1.5">Calculation Mode</label>
                                    <div className="grid grid-cols-3 gap-2 mb-2">
                                        {['PERCENT', 'ATR', 'FIXED'].map(type => (
                                            <button
                                                key={type}
                                                onClick={() => {
                                                    handleParamChange('sl_type', type);
                                                    //! Ensure sl_value is persisted — prevents silent fallback to backend default
                                                    if (nodeData.params?.sl_value == null) {
                                                        handleParamChange('sl_value', 1.0);
                                                    }
                                                }}
                                                className={`px-2 py-2 rounded-lg text-[10px] font-bold border transition-all ${
                                                    (nodeData.params?.sl_type || 'PERCENT') === type
                                                        ? 'bg-[var(--color-loss)] text-white border-[var(--color-loss)] shadow-md'
                                                        : 'bg-bg-2 text-fg-3 border-[var(--divider)] hover:bg-bg-3'
                                                }`}
                                            >
                                                {type}
                                            </button>
                                        ))}
                                    </div>
                                    
                                    {/* DYNAMIC HELP TEXT */}
                                    <div className="bg-[color-mix(in_oklch,var(--color-loss)_8%,transparent)] p-2 rounded border border-[color-mix(in_oklch,var(--color-loss)_30%,transparent)]">
                                        <p className="text-[10px] text-[var(--color-loss)] leading-snug">
                                            {(nodeData.params?.sl_type || 'PERCENT') === 'PERCENT' && (
                                                <><strong>Percentage:</strong> SL is placed at {nodeData.params?.sl_value ?? 1.0}% distance from Entry Price.</>
                                            )}
                                            {(nodeData.params?.sl_type || 'PERCENT') === 'ATR' && (
                                                <><strong>ATR Multiple:</strong> SL is placed at ({nodeData.params?.sl_value ?? 1.0} x ATR) distance from Entry Price. Uses a built-in ATR(14) fallback if no ATR feed is connected.</>
                                            )}
                                            {(nodeData.params?.sl_type || 'PERCENT') === 'FIXED' && (
                                                <><strong>Fixed Distance:</strong> SL is placed {nodeData.params?.sl_value ?? 1.0} price units away from Entry Price (not ticks/pips).</>
                                            )}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* CUSTOM UI — Trigger Source only; never touches input_0 / entry signal */}
                        {slMode === 'CUSTOM' && (
                            <div className="space-y-4 animate-fadeIn border-t border-[color-mix(in_oklch,var(--color-loss)_40%,transparent)] pt-4">
                                <div>
                                    <label className="block text-[10px] font-bold text-fg-3 uppercase mb-1.5">Trigger Logic <span className="text-[var(--warning)]">(Boolean)</span></label>
                                    <select
                                        className="w-full px-3 py-2.5 rounded-lg bg-bg-2 border border-[var(--divider)] text-xs font-bold outline-none focus:ring-2 focus:ring-[color-mix(in_oklch,var(--warning)_50%,transparent)] truncate"
                                        value={slTriggerSourceId}
                                        onChange={(e) => {
                                            const newSrcId = e.target.value;
                                            const newSrcNode = nodes.find(n => n.id === newSrcId);
                                            const rf = resolveSourceFields(newSrcNode, availableFields, allIndicatorMetadata);
                                            const df = rf[0] || 'value';
                                            commitOperandLink({
                                                inputs: nodeData.inputs,
                                                targetHandle: 'trigger_input',
                                                sourceId: newSrcId,
                                                field: df,
                                                persistInput: false,
                                                edgeStyle: { strokeWidth: 2, stroke: 'var(--warning)', strokeDasharray: '5,5' },
                                            });
                                        }}
                                    >
                                        <option value="">Select Trigger Source...</option>
                                        {availableSources.map(opt => (
                                            <option key={opt.id} value={opt.id}>
                                                {opt.type === 'comparator' ? '[COMP] ' :
                                                 opt.type === 'logic_gate' ? '[LOGIC] ' :
                                                 opt.data?.action === 'sl' ? '[SL] ' :
                                                 opt.data?.action === 'tp' ? '[TP] ' :
                                                 opt.data?.action === 'time_exit' ? '[TIME] ' : '[NODE] '}
                                                {opt.data?.label || opt.label}
                                            </option>
                                        ))}
                                    </select>
                                    {slTriggerSourceId && slTriggerFields.length > 1 && (
                                        <select
                                            className="mt-2 w-full px-3 py-2 rounded-lg bg-bg-2 border border-[var(--divider)] text-[10px] font-mono font-bold outline-none focus:ring-2 focus:ring-[color-mix(in_oklch,var(--warning)_50%,transparent)]"
                                            value={slTriggerField}
                                            onChange={(e) => {
                                                commitOperandField({
                                                    inputs: nodeData.inputs,
                                                    targetHandle: 'trigger_input',
                                                    field: e.target.value,
                                                    persistInput: false,
                                                });
                                            }}
                                        >
                                            {slTriggerFields.map(f => <option key={f} value={f}>{f.toUpperCase()}</option>)}
                                        </select>
                                    )}
                                    <p className="text-[10px] text-fg-3 mt-2 leading-relaxed">
                                        Connect a <strong>Comparator</strong> or <strong>Logic Gate</strong> to the <strong className="text-[var(--warning)]">amber Trigger handle</strong>. SL fires when TRUE.
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            );
        }

        // 2. TAKE PROFIT NODE
        if (rawAction === 'tp') {
            // Priority: Explicit exit_mode > Standard.
            // Entry association (input_0) should not auto-switch to CUSTOM.
            const tpMode = nodeData.params?.exit_mode || 'STANDARD';

            // Read-only entry signals: Auto-managed dynamically via active link inputs.
            const tpEntryNodes = resolveActiveEntryNodes();
            const hasTpEntries = tpEntryNodes.length > 0;

            // Trigger: wired exclusively to trigger_input (user-managed in CUSTOM mode)
            const tpTriggerEdge = edges.find(e =>
                e.target === liveNode.id && e.targetHandle === 'trigger_input'
            );
            const tpTriggerSourceId = tpTriggerEdge ? tpTriggerEdge.source : '';
            const tpTriggerNode = tpTriggerSourceId ? nodes.find(n => n.id === tpTriggerSourceId) : null;
            const tpTriggerFields = tpTriggerNode
                ? resolveSourceFields(tpTriggerNode, availableFields, allIndicatorMetadata)
                : [];
            const tpTriggerField = tpTriggerEdge?.sourceHandle || tpTriggerFields[0] || 'value';

            return (
                <div className="space-y-6 animate-fadeIn">
                    {renderNodeInfoSection('Risk Management', 'Logic Node')}
                    
                    {renderActionDisplay()}

                    <div className="bg-[color-mix(in_oklch,var(--color-profit)_10%,transparent)] p-4 rounded-xl border border-[color-mix(in_oklch,var(--color-profit)_30%,transparent)] space-y-4">
                        <div className="flex items-center gap-3 mb-2">
                            <FiTarget className="text-[var(--color-profit)]" size={20} />
                            <h4 className="text-sm font-bold text-[var(--color-profit)]">Take Profit Settings</h4>
                        </div>
                        
                        {renderExecutionStrategyToggle(tpMode)}

                        {/* ENTRY SIGNAL — always shown, read-only, auto-wired to matching entry */}
                        <div className="rounded-lg p-2.5 bg-bg-3 dark:bg-bg-2/60 border border-[var(--divider)] flex flex-col gap-2">
                            <div className="flex items-center justify-between">
                                <div className="text-[8px] font-bold text-fg-3 uppercase tracking-widest">Entry Signal (auto-wired)</div>
                                {hasTpEntries && <span className="text-[9px] font-bold text-[var(--node-comparator)] bg-[color-mix(in_oklch,var(--node-comparator)_10%,transparent)] px-1.5 py-0.5 rounded flex-shrink-0">WIRED</span>}
                            </div>
                            
                            {hasTpEntries ? (
                                <div className="space-y-1.5">
                                    {tpEntryNodes.map((n, idx) => (
                                        <div key={idx} className="flex items-center gap-2">
                                            <div className="w-2 h-2 rounded-full flex-shrink-0 bg-[var(--node-comparator)]" />
                                            <div className="text-[10px] font-bold text-fg-2 truncate flex-1 block">
                                                {n.data?.label || n.data?.action?.replace(/_/g,' ').toUpperCase() || 'Entry Node'}
                                            </div>
                                            {tpEntryNodes.length > 1 && (
                                                <span className="text-[8px] font-bold text-fg-3 bg-bg-3 dark:bg-bg-3 px-1 py-0.5 rounded whitespace-nowrap">STAGE {idx + 1}</span>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="flex items-center gap-2">
                                    <div className="w-2 h-2 rounded-full flex-shrink-0 bg-bg-3" />
                                    <span className="text-[10px] text-fg-3 italic font-normal">No entry node found — add an entry first</span>
                                </div>
                            )}
                        </div>

                        {/* REFERENCE PRICE — only shown when this TP is shared across multiple entries */}
                        {tpEntryNodes.length > 1 && (
                            <div className="rounded-lg p-2.5 bg-bg-3 dark:bg-bg-2/60 border border-[var(--divider)] space-y-2">
                                <div className="flex items-center gap-1.5">
                                    <label className="text-[10px] font-bold text-fg-3 uppercase tracking-widest">Reference Price</label>
                                    <div className="relative group">
                                        <span className="text-[9px] text-fg-3 cursor-help border border-[var(--border)] rounded-full w-3.5 h-3.5 inline-flex items-center justify-center font-bold">i</span>
                                        <div className="absolute left-5 top-0 z-50 hidden group-hover:block w-52 p-2 rounded-lg bg-bg-1 text-fg-1 border border-[var(--border)] text-[10px] leading-relaxed shadow-xl">
                                            {(nodeData.params?.reference_price || 'average') === 'average' && 'Uses the weighted average cost of all open stages. Industry standard.'}
                                            {nodeData.params?.reference_price === 'last' && "Uses the most recent stage's fill price."}
                                            {nodeData.params?.reference_price === 'first' && 'Uses the original entry price.'}
                                        </div>
                                    </div>
                                </div>
                                <select
                                    className="w-full px-3 py-2 rounded-lg bg-bg-2 border border-[var(--divider)] text-[10px] font-bold outline-none focus:ring-2 focus:ring-[color-mix(in_oklch,var(--color-profit)_50%,transparent)]"
                                    value={nodeData.params?.reference_price || 'average'}
                                    onChange={(e) => handleParamChange('reference_price', e.target.value)}
                                >
                                    <option value="average">Average (VWAP)</option>
                                    <option value="last">Last Entry</option>
                                    <option value="first">First Entry</option>
                                </select>
                            </div>
                        )}

                         <div>
                            <label className="block text-[10px] font-bold text-fg-3 uppercase mb-1.5">Exit Mode</label>
                            <div className="flex bg-bg-2 rounded-lg p-1 border border-[var(--divider)]">
                                {['STANDARD', 'CUSTOM'].map(mode => (
                                    <button
                                        key={mode}
                                        onClick={() => {
                                            const newParams = { 
                                                ...(nodeData.params || {}),
                                                exit_mode: mode
                                            };
                                            
                                            const updatePayload = { params: newParams };
                                            
                                            if (mode !== 'CUSTOM' && onEdgeUpdate) {
                                                const unlinked = linkOperandInput({
                                                    inputs: nodeData.inputs,
                                                    edges,
                                                    targetNode: liveNode,
                                                    targetId: liveNode.id,
                                                    targetHandle: 'trigger_input',
                                                    sourceId: null,
                                                    persistInput: false,
                                                });
                                                if (unlinked.edges.length !== edges.length) {
                                                    onEdgeUpdate(unlinked.edges);
                                                }
                                            }
                                            
                                            updateNodeData(selectedNode.id, updatePayload);
                                        }}
                                        className={`flex-1 py-1.5 rounded-md text-[10px] font-bold transition-all ${
                                            tpMode === mode
                                                ? 'bg-[var(--color-profit)] text-white shadow-sm'
                                                : 'text-fg-3 hover:text-fg-1 dark:hover:text-fg-3'
                                        }`}
                                    >
                                        {mode}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* EXIT MODE DESCRIPTION */}
                        <div className="rounded-lg p-2.5 text-[10px] leading-relaxed bg-bg-3 dark:bg-bg-2/60 border border-[var(--divider)] text-fg-3">
                            {tpMode === 'STANDARD' && <><span className="font-bold text-fg-2">Standard</span> — Self-contained. Fires automatically when price reaches the configured distance. No extra wiring needed.</>}
                            {tpMode === 'CUSTOM' && <><span className="font-bold text-[var(--warning)]">Custom</span> — External trigger. Connect a <span className="font-semibold">Comparator</span> or <span className="font-semibold">Logic Gate</span> to the <span className="text-[var(--warning)] font-bold">amber Trigger handle</span>. Exit fires when that condition is TRUE.</> }
                        </div>
                        
                        {/* STANDARD UI */}
                        {tpMode === 'STANDARD' && (
                            <div className="space-y-4 animate-fadeIn">
                                {/* TP Value */}
                                <div>
                                    <label className="block text-[10px] font-bold text-fg-3 uppercase mb-1.5">Take Profit Distance</label>
                                    <input
                                        type="number"
                                        step="0.1"
                                        value={nodeData.params?.tp_value ?? 2.0}
                                        onChange={(e) => handleParamChange('tp_value', parseFloat(e.target.value))}
                                        className="w-full px-3 py-2 rounded-lg bg-bg-2 border border-[var(--divider)] text-sm font-bold outline-none focus:ring-2 focus:ring-[color-mix(in_oklch,var(--color-profit)_50%,transparent)]"
                                    />
                                </div>

                                {/* TP Type */}
                                <div>
                                    <label className="block text-[10px] font-bold text-fg-3 uppercase mb-1.5">Calculation Mode</label>
                                    <div className="grid grid-cols-3 gap-2 mb-2">
                                        {['PERCENT', 'ATR', 'FIXED'].map(type => (
                                            <button
                                                key={type}
                                                onClick={() => {
                                                    handleParamChange('tp_type', type);
                                                    //! Ensure tp_value is persisted — prevents silent fallback to backend default
                                                    if (nodeData.params?.tp_value == null) {
                                                        handleParamChange('tp_value', 2.0);
                                                    }
                                                }}
                                                className={`px-2 py-2 rounded-lg text-[10px] font-bold border transition-all ${
                                                    (nodeData.params?.tp_type || 'PERCENT') === type
                                                        ? 'bg-[var(--color-profit)] text-white border-[var(--color-profit)] shadow-md'
                                                        : 'bg-bg-2 text-fg-3 border-[var(--divider)] hover:bg-bg-3'
                                                }`}
                                            >
                                                {type}
                                            </button>
                                        ))}
                                    </div>

                                    {/* DYNAMIC HELP TEXT */}
                                    <div className="bg-[color-mix(in_oklch,var(--color-profit)_8%,transparent)] p-2 rounded border border-[color-mix(in_oklch,var(--color-profit)_30%,transparent)]">
                                        <p className="text-[10px] text-[var(--color-profit)] leading-snug">
                                            {(nodeData.params?.tp_type || 'PERCENT') === 'PERCENT' && (
                                                <><strong>Percentage:</strong> TP is placed at {nodeData.params?.tp_value ?? 2.0}% distance from Entry Price.</>
                                            )}
                                            {(nodeData.params?.tp_type || 'PERCENT') === 'ATR' && (
                                                <><strong>ATR Multiple:</strong> TP is placed at ({nodeData.params?.tp_value ?? 2.0} x ATR) distance from Entry Price. Uses a built-in ATR(14) fallback if no ATR feed is connected.</>
                                            )}
                                            {(nodeData.params?.tp_type || 'PERCENT') === 'FIXED' && (
                                                <><strong>Fixed Distance:</strong> TP is placed {nodeData.params?.tp_value ?? 2.0} price units away from Entry Price (not ticks/pips).</>
                                            )}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* CUSTOM UI — Trigger Source only; never touches input_0 / entry signal */}
                        {tpMode === 'CUSTOM' && (
                            <div className="space-y-4 animate-fadeIn border-t border-[color-mix(in_oklch,var(--color-profit)_40%,transparent)] pt-4">
                                <div>
                                    <label className="block text-[10px] font-bold text-fg-3 uppercase mb-1.5">Trigger Logic <span className="text-[var(--warning)]">(Boolean)</span></label>
                                    <select
                                        className="w-full px-3 py-2.5 rounded-lg bg-bg-2 border border-[var(--divider)] text-xs font-bold outline-none focus:ring-2 focus:ring-[color-mix(in_oklch,var(--warning)_50%,transparent)] truncate"
                                        value={tpTriggerSourceId}
                                        onChange={(e) => {
                                            const newSrcId = e.target.value;
                                            const newSrcNode = nodes.find(n => n.id === newSrcId);
                                            const rf = resolveSourceFields(newSrcNode, availableFields, allIndicatorMetadata);
                                            const df = rf[0] || 'value';
                                            commitOperandLink({
                                                inputs: nodeData.inputs,
                                                targetHandle: 'trigger_input',
                                                sourceId: newSrcId,
                                                field: df,
                                                persistInput: false,
                                                edgeStyle: { strokeWidth: 2, stroke: 'var(--warning)', strokeDasharray: '5,5' },
                                            });
                                        }}
                                    >
                                        <option value="">Select Trigger Source...</option>
                                        {availableSources.map(opt => (
                                            <option key={opt.id} value={opt.id}>
                                                {opt.type === 'comparator' ? '[COMP] ' :
                                                 opt.type === 'logic_gate' ? '[LOGIC] ' :
                                                 opt.data?.action === 'sl' ? '[SL] ' :
                                                 opt.data?.action === 'tp' ? '[TP] ' :
                                                 opt.data?.action === 'time_exit' ? '[TIME] ' : '[NODE] '}
                                                {opt.data?.label || opt.label}
                                            </option>
                                        ))}
                                    </select>
                                    {tpTriggerSourceId && tpTriggerFields.length > 1 && (
                                        <select
                                            className="mt-2 w-full px-3 py-2 rounded-lg bg-bg-2 border border-[var(--divider)] text-[10px] font-mono font-bold outline-none focus:ring-2 focus:ring-[color-mix(in_oklch,var(--warning)_50%,transparent)]"
                                            value={tpTriggerField}
                                            onChange={(e) => {
                                                commitOperandField({
                                                    inputs: nodeData.inputs,
                                                    targetHandle: 'trigger_input',
                                                    field: e.target.value,
                                                    persistInput: false,
                                                });
                                            }}
                                        >
                                            {tpTriggerFields.map(f => <option key={f} value={f}>{f.toUpperCase()}</option>)}
                                        </select>
                                    )}
                                    <p className="text-[10px] text-fg-3 mt-2 leading-relaxed">
                                        Connect a <strong>Comparator</strong> or <strong>Logic Gate</strong> to the <strong className="text-[var(--warning)]">amber Trigger handle</strong>. TP fires when TRUE.
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            );
        }

        // 3. TIME EXIT NODE
        if (rawAction === 'time_exit') {
            return (
                <div className="space-y-6 animate-fadeIn">
                    {renderNodeInfoSection('Time Logic', 'Logic Node')}
                    
                    {renderActionDisplay()}

                    <div className="bg-[color-mix(in_oklch,var(--warning)_10%,transparent)] p-4 rounded-xl border border-[color-mix(in_oklch,var(--warning)_30%,transparent)] space-y-4">
                        <div className="flex items-center gap-3 mb-2">
                            <FiClock className="text-[var(--warning)]" size={20} />
                            <h4 className="text-sm font-bold text-[var(--warning)]">Time Exit Settings</h4>
                        </div>
                        
                        {/* Max Bars */}
                        <div>
                            <label className="block text-[10px] font-bold text-fg-3 uppercase mb-1.5">Max Bars Held</label>
                            <input
                                type="number"
                                min="1"
                                step="1"
                                value={nodeData.params?.max_bars ?? 10}
                                onChange={(e) => handleParamChange('max_bars', parseInt(e.target.value))}
                                className="w-full px-3 py-2 rounded-lg bg-bg-2 border border-[var(--divider)] text-sm font-bold outline-none focus:ring-2 focus:ring-[color-mix(in_oklch,var(--warning)_50%,transparent)]"
                            />
                            <p className="text-[10px] text-fg-3 mt-1">Position closes automatically after this many bars.</p>
                        </div>
                    </div>
                </div>
            );
        }

        // 4. GENERIC SIGNAL / EXIT NODE
        // Exit signals (exit_long, exit_short, time_exit) have TWO handles:
        //   input_0      — Signal: auto-wired to the matching entry node (read-only).
        //   trigger_input — Trigger: user connects a Comparator/Logic Gate here.
        // Plain entry nodes (enter_long, enter_short, buy, sell) use input_0 for the
        // trigger source as before.
        const isExitSignalNode = ['exit_long', 'exit_short', 'time_exit'].includes(rawAction);

        // For exit nodes: read what is wired to trigger_input from the edge list directly.
        const triggerEdge = isExitSignalNode
            ? edges.find(e => e.target === liveNode.id && e.targetHandle === 'trigger_input')
            : null;
        const triggerSourceId = triggerEdge ? triggerEdge.source : '';
        const triggerSourceNode = triggerSourceId ? nodes.find(n => n.id === triggerSourceId) : null;
        const triggerFields = triggerSourceNode
            ? resolveSourceFields(triggerSourceNode, availableFields, allIndicatorMetadata)
            : [];
        const triggerField = triggerEdge?.sourceHandle || triggerFields[0] || 'value';

        // Entry nodes wired to the source handles (auto-managed dynamically).
        const signalEntryNodes = isExitSignalNode ? resolveActiveEntryNodes() : [];
        const hasSignalEntries = signalEntryNodes.length > 0;

        return (
            <div className="space-y-6 animate-fadeIn">
                {renderNodeInfoSection('Execution Trigger', 'Signal Node')}

                {renderActionDisplay()}
                
                {renderExecutionStrategyToggle()}

                {/* ── EXIT SIGNAL NODES: two-section layout ── */}
                {isExitSignalNode ? (
                    <>
                        {/* 4a. Entry Signal — auto-wired, read-only */}
                        <div className="space-y-2">
                            <label className="block text-[10px] font-bold text-fg-3 uppercase tracking-widest px-1">
                                ENTRY SIGNAL (AUTO-WIRED)
                            </label>
                            <div className="bg-bg-3 dark:bg-bg-1/50 p-3 rounded-xl border border-[var(--divider)] flex flex-col gap-2">
                                <div className="flex items-center justify-between pb-1 mb-1 border-b border-[var(--divider)]">
                                    <span className="text-[8px] font-bold text-fg-3 uppercase tracking-widest">Entry Sources</span>
                                    {hasSignalEntries && <span className="text-[9px] font-bold text-[var(--node-comparator)] bg-[color-mix(in_oklch,var(--node-comparator)_10%,transparent)] px-1.5 py-0.5 rounded flex-shrink-0">WIRED</span>}
                                </div>
                                
                                {hasSignalEntries ? (
                                    <div className="space-y-2">
                                        {signalEntryNodes.map((n, idx) => (
                                            <div key={idx} className="flex items-center gap-3">
                                                <div className="w-2 h-2 rounded-full bg-[var(--node-comparator)] flex-shrink-0" />
                                                <span className="text-xs font-bold text-fg-2 truncate flex-1 block">
                                                    {n.data?.label || n.data?.action?.replace('_',' ').toUpperCase() || 'Entry Node'}
                                                </span>
                                                {signalEntryNodes.length > 1 && (
                                                    <span className="ml-auto text-[8px] font-bold text-fg-3 bg-bg-3 dark:bg-bg-2 px-1.5 py-0.5 rounded whitespace-nowrap">STAGE {idx + 1}</span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-2">
                                        <div className="w-2 h-2 rounded-full bg-bg-3 flex-shrink-0" />
                                        <span className="text-xs text-fg-3 italic">No entry node wired yet</span>
                                    </div>
                                )}
                            </div>
                            <p className="text-[9px] text-fg-3 px-1">
                                This is automatically set to the matching entry node. Add an entry node to wire it.
                            </p>
                        </div>

                        {/* 4b. Trigger Source — user wires a comparator/logic gate */}
                        <div className="space-y-2">
                            <label className="block text-[10px] font-bold text-fg-3 uppercase tracking-widest px-1">
                                TRIGGER SOURCE <span className="text-[var(--warning)]">(BOOLEAN)</span>
                            </label>
                            <div className="bg-bg-3 dark:bg-bg-1/50 p-4 rounded-xl border border-[var(--divider)] space-y-4 shadow-inner">
                                <div>
                                    <label className="block text-[8px] font-bold text-fg-3 uppercase mb-1.5">Logic Node (Comparator / Gate)</label>
                                    <select
                                        className="w-full px-3 py-2.5 rounded-lg bg-bg-2 border border-[var(--divider)] text-xs font-bold outline-none focus:ring-2 focus:ring-[color-mix(in_oklch,var(--warning)_50%,transparent)] truncate"
                                        value={triggerSourceId}
                                        onChange={(e) => {
                                            const newSrcId = e.target.value;
                                            const newSrcNode = nodes.find(n => n.id === newSrcId);
                                            const resolvedF = resolveSourceFields(newSrcNode, availableFields, allIndicatorMetadata);
                                            const defField  = resolvedF[0] || 'value';
                                            commitOperandLink({
                                                inputs: nodeData.inputs,
                                                targetHandle: 'trigger_input',
                                                sourceId: newSrcId,
                                                field: defField,
                                                persistInput: false,
                                                edgeStyle: { strokeWidth: 2, stroke: 'var(--warning)', strokeDasharray: '5,5' },
                                            });
                                        }}
                                    >
                                        <option value="">Select Trigger Source...</option>
                                        {availableSources.map(opt => (
                                            <option key={opt.id} value={opt.id}>
                                                {opt.type === 'comparator' ? '[COMP] ' :
                                                 opt.type === 'logic_gate' ? '[LOGIC] ' : '[NODE] '}
                                                {opt.data?.label || opt.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {triggerSourceId && triggerFields.length > 1 && (
                                    <div className="animate-fadeIn">
                                        <label className="block text-[8px] font-bold text-fg-3 uppercase mb-1.5">Source Output</label>
                                        <select
                                            className="w-full px-3 py-2 rounded-lg bg-bg-2 border border-[var(--border)] text-[10px] font-mono font-bold outline-none focus:ring-2 focus:ring-[color-mix(in_oklch,var(--warning)_50%,transparent)]"
                                            value={triggerField}
                                            onChange={(e) => {
                                                const newF = e.target.value;
                                                commitOperandField({
                                                    inputs: nodeData.inputs,
                                                    targetHandle: 'trigger_input',
                                                    field: newF,
                                                    persistInput: false,
                                                });
                                            }}
                                        >
                                            {triggerFields.map(f => <option key={f} value={f}>{f.toUpperCase()}</option>)}
                                        </select>
                                    </div>
                                )}

                                {/* Qty % */}
                                <div className="pt-2 border-t border-[var(--divider)] mt-2">
                                    <label className="block text-[8px] font-bold text-fg-3 uppercase mb-1.5">Exit Quantity</label>
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="number"
                                            min="1" max="100"
                                            value={nodeData.params?.qty_pct ?? 100}
                                            onChange={(e) => handleParamChange('qty_pct', parseInt(e.target.value))}
                                            className="w-full px-3 py-2 rounded-lg bg-bg-2 border border-[var(--divider)] text-sm font-bold outline-none"
                                        />
                                        <span className="text-xs font-bold text-fg-3">%</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="p-3 bg-[color-mix(in_oklch,var(--warning)_10%,transparent)] rounded-lg border border-[color-mix(in_oklch,var(--warning)_30%,transparent)]">
                            <p className="text-[10px] text-[var(--warning)] leading-relaxed font-medium">
                                <strong>Signal</strong> (top handle) auto-wires to the matched entry node — it defines <em>which</em> position to exit.
                                Connect a Comparator or Logic Gate to the <strong className="text-[var(--warning)]">amber Trigger</strong> handle to define <em>when</em> to exit.
                            </p>
                        </div>
                    </>
                ) : (
                    /* ── ENTRY / PLAIN SIGNAL NODES: original single-source layout ── */
                    <>
                        <div className="space-y-4">
                            <label className="block text-[10px] font-bold text-fg-3 uppercase tracking-widest px-1">
                                TRIGGER SOURCE (BOOLEAN)
                            </label>

                            <div className="bg-bg-3 dark:bg-bg-1/50 p-4 rounded-xl border border-[var(--divider)] space-y-4 shadow-inner">
                                <div>
                                    <label className="block text-[8px] font-bold text-fg-3 uppercase mb-1.5">Logic Node (Comparator/Gate)</label>
                                    <select
                                        className="w-full px-3 py-2.5 rounded-lg bg-bg-2 border border-[var(--divider)] text-xs font-bold outline-none focus:ring-2 focus:ring-[color-mix(in_oklch,var(--node-comparator)_50%,transparent)] truncate"
                                        value={currentSourceId}
                                        onChange={(e) => {
                                            const newSourceId = e.target.value;
                                            const newSourceNode = nodes.find(n => n.id === newSourceId);
                                            const resolvedFields = resolveSourceFields(newSourceNode, availableFields, allIndicatorMetadata);
                                            const defaultField = resolvedFields[0] || 'value';
                                            commitOperandLink({
                                                inputs: [input],
                                                inputIndex: 0,
                                                targetHandle: handleId,
                                                sourceId: newSourceId,
                                                field: defaultField,
                                                edgeStyle: { strokeWidth: 2, stroke: 'var(--node-comparator)' },
                                            });
                                        }}
                                    >
                                        <option value="">Select Trigger Source...</option>
                                        {availableSources.map(opt => (
                                            <option key={opt.id} value={opt.id}>
                                                {opt.type === 'comparator' ? '[COMP] ' :
                                                 opt.type === 'logic_gate' ? '[LOGIC] ' :
                                                 opt.type === 'signal' && opt.data?.action === 'sl' ? '[SL] ' :
                                                 '[NODE] '}
                                                {opt.data?.label || opt.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {currentSourceId && fields.length > 1 && (
                                    <div className="animate-fadeIn">
                                        <label className="block text-[8px] font-bold text-fg-3 uppercase mb-1.5">Source Output</label>
                                        <select
                                            className="w-full px-3 py-2 rounded-lg bg-bg-2 border border-[var(--border)] text-[10px] font-mono font-bold outline-none focus:ring-2 focus:ring-[color-mix(in_oklch,var(--node-comparator)_50%,transparent)]"
                                            value={currentField}
                                            onChange={(e) => {
                                                const newField = e.target.value;
                                                commitOperandField({
                                                    inputs: [input],
                                                    inputIndex: 0,
                                                    targetHandle: handleId,
                                                    field: newField,
                                                });
                                            }}
                                        >
                                            {fields.map(f => <option key={f} value={f}>{f.toUpperCase()}</option>)}
                                        </select>
                                    </div>
                                )}

                                <div>
                                    <label className="block text-[8px] font-bold text-fg-3 uppercase mb-1.5">Lookback Offset</label>
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="number"
                                            min="0"
                                            value={input.offset || 0}
                                            onChange={(e) => handleInputChange('offset', e.target.value)}
                                            className="w-full px-3 py-2 rounded-lg bg-bg-2 border border-[var(--divider)] text-xs font-mono font-bold outline-none tabular-nums"
                                        />
                                        <span className="text-[10px] font-bold text-fg-3 uppercase tracking-tighter">Bars</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="p-3 bg-[color-mix(in_oklch,var(--node-comparator)_10%,transparent)] rounded-lg border border-[color-mix(in_oklch,var(--node-comparator)_30%,transparent)]">
                            <p className="text-[10px] text-[var(--node-comparator)] leading-relaxed font-medium">
                                This signal triggers only when the connected logic source becomes <strong>TRUE</strong>. Use Comparators to define your entry/exit conditions.
                            </p>
                        </div>
                    </>
                )}
            </div>
        );
    };

    // --> Default/Fallback Panel
    const renderDefaultPanel = () => (
        <div className="text-center py-10 text-fg-3 italic">
            Configuration available soon.
        </div>
    );

    // --> Render appropriate panel based on node type
    // --> About disclosure (slice 3/6): the SHARED IndicatorAboutSection, keyed by node id so a
    //*  node switch remounts it collapsed; rendered by the page footer AFTER ChartStylingSection
    const renderPanelContent = () => {
        switch(nodeType) {
            case 'comparator':
                return renderComparatorPanel();
            case 'logic_gate':
                return renderLogicGatePanel();
            case 'time_window':
                return renderTimeWindowPanel();
            case 'math':
                return renderMathPanel();
            case 'signal':
            case 'position_trigger':
                return renderSignalPanel();
            case 'data_source':
                return <DataSourcePanel selectedNode={selectedNode} nodeData={nodeData} updateNodeData={updateNodeData} />;
            case 'candle_node':
            case 'heikin_node':
            case 'volume_node':
                return renderCandlePanel();
            case 'line_node':
                return renderLinePanel();
            case 'area_node':
                return renderAreaPanel();
            case 'indicator':
                return renderIndicatorPanel();
            case 'chart_node':
            case 'chart_output':
            case 'metadata':
                return (
                    <div className="bg-bg-3 dark:bg-bg-2 rounded-lg p-4 border border-[var(--divider)]">
                        <div className="flex items-center gap-3 mb-4 text-fg-3">
                            <FiActivity />
                            <span className="text-xs font-bold uppercase">Multi-Layer Visualization</span>
                        </div>
                        <p className="text-xs text-fg-3 leading-relaxed">
                            This node acts as a <strong>Container</strong> for your chart layers.
                            <br/><br/>
                            Connect <strong>Candlestick</strong>, <strong>Line</strong>, or <strong>Indicator</strong> nodes to this output to visualize them stacked on the same panel.
                            <br/><br/>
                            <em className="opacity-75">No configuration required. The chart automatically renders all connected inputs.</em>
                        </p>
                    </div>
                );
            default:
                return renderDefaultPanel();
        }
    };

    // --> Charts Styling Logic moved to ChartStylingSection.jsx

    return (
        <div className="space-y-6 animate-fadeIn pb-20">
            {/* Header */}
            <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-extrabold text-fg-1 tracking-wide uppercase flex items-center gap-2">
                    <FiSliders className="text-[var(--color-accent)]" />
                    Properties: {getPanelTitle()}
                </h3>
                <Tooltip content="Close Properties" placement="left">
                <button
                    onClick={onClose}
                    className="p-1 hover:bg-bg-3 dark:hover:bg-bg-2 rounded text-fg-3 hover:text-fg-2 transition-colors"
                    aria-label="Close Properties"
                >
                    <FiX size={16} />
                </button>
                </Tooltip>
            </div>

            {/* Subtitle */}
            <p className="text-xs text-fg-3 mb-4">
                Configuring: <span className="font-mono font-bold text-[var(--node-visual)]">{getNodeDisplayName()}</span>
            </p>

            {linkRefusal && (
                <p role="status" className="text-xs text-[var(--color-loss)] mb-4">{linkRefusal}</p>
            )}
            
            {/* R9 — condition Name + Notes (comparator / logic_gate / time_window only) */}
            {CONDITION_NAME_NODE_TYPES.includes(nodeType) && (
                <ConditionNameNotesFields
                    nodeId={selectedNode.id}
                    strategyId={strategyId}
                    entry={visualOverrides?.[selectedNode.id]}
                    legacyName={resolveAuthoredName(liveNode, undefined)}
                    autoText={resolveAutoText(liveNode, nodes, edges, 0, visualOverrides || undefined)}
                    updateNodeData={updateNodeData}
                    lockHint={displayLockHint}
                />
            )}

            {/* Panel Content */}
            {renderPanelContent()}

            {/* Reusable Chart Styling Component. A-3: a saved strategy whose display record is not writable
                (load failed / read-only) shows the controls disabled with the hint — the writes are also
                refused at the store, this is the visible half. */}
            {displayLockHint && (
                <p className="text-[10px] text-fg-3 italic mt-3" data-testid="display-lock-hint">{displayLockHint}</p>
            )}
            <div aria-disabled={displayLockHint ? 'true' : undefined} className={displayLockHint ? 'pointer-events-none opacity-60' : undefined}>
                <ChartStylingSection
                    nodeType={nodeType}
                    nodeData={nodeData}
                    updateNodeData={updateNodeData}
                    nodeId={selectedNode.id}
                    allNodes={nodes}
                    defaultOutputStyles={defaultOutputStyles(nodeData.indicator, allIndicatorMetadata, nodeData.visual_settings, nodeData.outputs)}
                />
            </div>

            {/* About disclosure: reference docs last (indicator nodes only) */}
            {nodeType === 'indicator' && (
                <IndicatorAboutSection key={selectedNode.id} nodeId={selectedNode.id} nodeData={nodeData} allIndicatorMetadata={allIndicatorMetadata} displayName={getNodeDisplayName()} />
            )}

            {/* Footer: Node ID */}
            <div className="mt-8 pt-4 border-t border-[var(--divider)] flex justify-between items-center opacity-60 hover:opacity-100 transition-opacity">
                <span className="text-[10px] text-fg-3 font-bold uppercase tracking-widest">Node ID</span>
                <span className="font-mono text-[10px] text-fg-3 bg-bg-3 dark:bg-bg-2 px-2 py-1 rounded select-all">{selectedNode.id}</span>
            </div>
        </div>
    );
};

// --> Extracted Data Source Panel Component
const DataSourcePanel = React.memo(({ selectedNode, nodeData, updateNodeData }) => {

    // Parse current timeframe
    const tf = nodeData.timeframe || '5m';
    const isCustom = !TIMEFRAME_OPTIONS.includes(tf);
    const [customTf, setCustomTf] = useState(isCustom ? parseInt(tf) : 5);

    const handleCustomTfChange = (val) => {
        const num = parseInt(val);
        setCustomTf(num);
        if (num > 0) {
            updateNodeData(selectedNode.id, { timeframe: `${num}m` });
        }
    };

    return (
        <div className="space-y-6">
            {/* Instrument Name (Label) */}
            <div>
                <label className="block text-xs font-bold text-fg-3 uppercase tracking-wider mb-2">
                    Instrument / Label
                </label>
                <input
                    type="text"
                    value={nodeData.label || 'Market Data'}
                    onChange={(e) => updateNodeData(selectedNode.id, { label: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-lg bg-bg-3 dark:bg-bg-2 border border-[var(--divider)] text-sm font-bold text-fg-1 focus:ring-2 focus:ring-[var(--node-visual)] outline-none"
                    placeholder="e.g. NIFTY 50"
                />
            </div>

            {/* Timeframe Configuration */}
            <div>
                <label className="block text-xs font-bold text-fg-3 uppercase tracking-wider mb-2">Timeframe</label>
                <div className="flex flex-wrap gap-2 mb-3">
                    {/* Presets */}
                    {['1m', '5m', '15m', '1h', '1d'].map((preset) => (
                        <button
                            key={preset}
                            onClick={() => updateNodeData(selectedNode.id, { timeframe: preset })}
                            className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-all ${
                                nodeData.timeframe === preset
                                    ? 'bg-[var(--node-visual)] border-[var(--node-visual)] text-white shadow-sm'
                                    : 'bg-bg-2 border-[var(--divider)] text-fg-2 hover:border-[var(--node-visual)]'
                            }`}
                        >
                            {preset.toUpperCase()}
                        </button>
                    ))}
                </div>
                
                {/* Custom Input */}
                <div className="flex items-center gap-3 bg-bg-3 dark:bg-bg-2/50 p-3 rounded-lg border border-[var(--divider)]">
                        <div className="flex-1">
                            <label className="block text-[10px] font-bold text-fg-3 uppercase tracking-wide mb-1">Custom Interval (min)</label>
                            <input
                            type="number"
                            min="1"
                            value={customTf}
                            onChange={(e) => handleCustomTfChange(e.target.value)}
                            className="w-full bg-bg-2 border border-[var(--divider)] rounded px-2 py-1 text-sm font-mono font-bold outline-none focus:border-[var(--node-visual)]"
                            />
                        </div>
                        <div className="text-xs font-medium text-fg-3 self-end mb-1">
                            Result: <span className="font-mono text-fg-1 font-bold">{customTf}m</span>
                        </div>
                </div>
            </div>

            {/* Read-Only Field List */}
            <div>
                <label className="block text-xs font-bold text-fg-3 uppercase tracking-wider mb-2">Available Fields</label>
                <div className="bg-bg-3 dark:bg-bg-2 border border-[var(--divider)] rounded-lg p-3">
                        <p className="text-[10px] text-fg-3 mb-2 uppercase tracking-wide font-bold">Standard OHLCV</p>
                        <div className="flex flex-wrap gap-2 mb-3">
                        {['OPEN', 'HIGH', 'LOW', 'CLOSE', 'VOLUME'].map(f => (
                            <span key={f} className="text-xs font-mono bg-bg-3 border border-[var(--border)] px-2 py-1 rounded text-fg-2">
                                {f}
                            </span>
                        ))}
                        </div>
                        
                        <p className="text-[10px] text-fg-3 mb-2 uppercase tracking-wide font-bold">Calculated</p>
                        <div className="flex flex-wrap gap-2">
                        {['VWAP', 'TRADE_COUNT', 'HL2', 'HLC3', 'OHLC4'].map(f => (
                            <span key={f} className="text-xs font-mono bg-[color-mix(in_oklch,var(--node-visual)_10%,transparent)] border border-[color-mix(in_oklch,var(--node-visual)_30%,transparent)] px-2 py-1 rounded text-[var(--node-visual)]">
                                {f}
                            </span>
                        ))}
                        </div>
                </div>
                <p className="text-[10px] text-fg-3 mt-2 italic">
                    * This node provides detailed market data structures. Use Field Extractors to isolate specific values.
                </p>
            </div>

            {/* Footer: Node ID */}
            <div className="mt-8 pt-4 border-t border-[var(--divider)] flex justify-between items-center opacity-60 hover:opacity-100 transition-opacity">
                <span className="text-[10px] text-fg-3 font-bold uppercase tracking-widest">Node ID</span>
                <span className="font-mono text-[10px] text-fg-3 bg-bg-3 dark:bg-bg-2 px-2 py-1 rounded select-all">{selectedNode.id}</span>
            </div>
        </div>
    );
});


// Export memoized version
export default React.memo(NodePropertiesPanel);
